/*!
* StreamingGlobal messaging client
* Copyright(c) 2020-2022  Streaming Global, Inc.
* Author:  William Ortega (wortega@streamingglobal.com)
*/

//import { Profile, csvLogger } from '@sg/profile';
import { AutoResetEvent, LocalClock, SGUtility } from '@sg/utility';

// TODO: circularity
//import { Debugs, IndexCode } from '@sg/BlazorShim';
//import { SGMessageClientManager } from './SGMessageClientManager';

// Useful for saving off files from blazor.  Add 'export' to enable
export function saveAsFile(filename: string, bytesBase64: string) {
    const link = document.createElement('a');
    link.download = filename;
    link.href = "data:application/octet-stream;base64," + bytesBase64;
    document.body.appendChild(link); // Needed for Firefox
    link.click();
    document.body.removeChild(link);
}

export function convertBlobToArrayBuffer(blob: Blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = function () {
            resolve(reader.result);
        };
        reader.onerror = function () {
            reject(new Error("failed to read blob as ArrayBuffer"));
        };
        reader.readAsArrayBuffer(blob);
    });
}

const SGMessagingClientInstance: Map<number, SGMessagingClientF> = new Map();
let NextIndex = 0;

// Ported in from CS -- BIG WIP
export class Mode {
    private static _HighEfficiency = 0;         // High efficiency which sleeps between processing calls.  Has reduced CPU utilization
    private static _HighPerformance = 1;         // High performance which yields between processing calls.  Has reduced message latency
    constructor() {
    }

    // Accessors for "get" functions only (no "set" functions)
    static get HighEfficiency() { return this._HighEfficiency; }
    static get HighPerformance() { return this._HighPerformance; }
}

export class SGSubscriptionMessage
{
    private __ClientId: number;
    private __Channel: string;

    get ClientId() { return this.__ClientId; }
    get Channel() { return this.__Channel; }
    constructor(clientId: number, channel: string)
    {
        this.__ClientId = clientId;
        this.__Channel = channel;
    }
}

export class SGTextMessage
{
    private __ClientId: number;
    private __Channel: string;
    private __Message: string;

    get ClientId() { return this.__ClientId; }
    get Channel() { return this.__Channel; }
    get Message() { return this.__Message; }

    constructor(clientId: number, channel: string, message: string)
    {
        this.__ClientId = clientId;
        this.__Channel = channel;
        this.__Message = message;
    }
}

export class SGBinaryMessage
{
    private __ClientId: number;
    private __Channel: string;
    private __Message: Uint8Array;

    get ClientId() { return this.__ClientId; }
    get Channel() { return this.__Channel; }
    get Message() { return this.__Message; }

    constructor(clientId: number, channel: string, message: Uint8Array) {
        this.__ClientId = clientId;
        this.__Channel = channel;
        if (message instanceof Uint8Array) {
            this.__Message = new Uint8Array(message.length);
            this.__Message.set(message, 0);
        }
    }
}

export class SGErrorMessage
{
    private __ClientId: number;
    private __ErrorMsg: string;

    get ClientId() { return this.__ClientId; }
    get ErrorMsg() { return this.__ErrorMsg; }

    constructor(clientId: number, errorMsg: string)
    {
        this.__ClientId = clientId;
        this.__ErrorMsg = errorMsg;
    }
}

export class SGTimeResponseMessage
{
    private __ClientId: number;
    private __Channel: string;
    private __Ticks: number;

    get ClientId() { return this.__ClientId; }
    get Channel() { return this.__Channel; }
    get Ticks() { return this.__Ticks; }

    constructor(clientId: number, channel: string, ticks: number)
    {
        this.__ClientId = clientId;
        this.__Channel = channel;
        this.__Ticks = ticks;
    }
}

export class SGTimeErrorResponseMessage
{
    private __ClientId: number;
    private __Channel: string;
    private __ErrorTicks: number;
    private __ResponseTicks: number;

    get ClientId() { return this.__ClientId; }
    get Channel() { return this.__Channel; }
    get ErrorTicks() { return this.__ErrorTicks; }
    get ResponseTicks() { return this.__ResponseTicks; }

    constructor(clientId: number, channel: string, errorTicks: number, responseTicks: number)
    {
        this.__ClientId = clientId;
        this.__Channel = channel;
        this.__ErrorTicks = errorTicks;
        this.__ResponseTicks = responseTicks;
    }
}

export class SGNetworkLBMessage {
    IncomingChannel = "";
    OutgoingSRCTransmittedTicks = 0;
    OutgoingRTSReceivedTicks = 0;
    OutgoingRTSTransmittedTicks = 0;
    OutgoingDSTReceivedTicks = 0;
    IncomingDSTTransmittedTicks = 0;
    IncomingRTSReceivedTicks = 0;
    IncomingRTSTransmittedTicks = 0;
    IncomingSRCReceivedTicks = 0;
    DataBytes = 0;
}

/// <summary>
/// Message class containing information necessary for the SG-RT protocol
/// </summary>
export class SG_RT_Message
{
    private __ClientId: number;
    private __Channel: string;
    private __PayloadType: string; // TODO candidate for better typing
    private __PayloadVersion: string; // TODO candidate for better typing
    private __PresentationTime: string;
    private __Data: string; // TODO determine type
    private __RawData: Uint8Array; // TODO determine type (most likely isn't Uint8Array since these don't have a .stream() method which is being called on this variable);


    get ClientId() { return this.__ClientId; }
    get Channel() { return this.__Channel; }
    get PayloadType() { return this.__PayloadType; }
    get PayloadVersion() { return this.__PayloadVersion; }
    get PresentationTime() { return this.__PresentationTime; }
    get Data() { return this.__Data; }
    get RawData() { return this.__RawData; }
    get IsText() { return (this.__RawData == null); }

    constructor(clientId: number, channel: string, payloadType: string, payloadVersion: string, presentationTime: string, data: string | Uint8Array)
    {
        this.__ClientId = clientId;
        this.__Channel = channel;
        this.__PayloadType = payloadType;
        this.__PayloadVersion = payloadVersion;
        this.__PresentationTime = presentationTime;
        if (data instanceof Uint8Array) {
///            this.__RawData = new Uint8Array(data.length);
///            this.__RawData.set(data, 0);
            this.__RawData = data;
            this.__Data = null;
        }
        else {
            this.__Data = data;
            this.__RawData = null;
        }
    }
}

export type SG_Any_Message = SGTextMessage | SGBinaryMessage;

export class SGMessagingClient
{
//    private readonly AsyncLazy < IJSObjectReference > moduleTask;
//    private TaskCompletionSource < bool > timeRspAvailable;
//    private TaskCompletionSource < bool > timeErrRspAvailable;
//    private TaskCompletionSource < bool > networkLBRspAvailable;
    LOCAL_DELTA_TIME_CALL: string;
    sgmc: SGMessagingClient;
    timeRspQueue: SGTimeResponseMessage[];
    timeErrRspQueue: SGTimeErrorResponseMessage[] = [];
    // TODO: Determine how to type this because we use it in two different ways, once pushing an SGMessageClientManager and another with a ViewerInstance (IndexCode)
    OnConnected = [];             // TODO determine type -> [SGMessageClientManager, (SGMessagingClient, clientId)]
    OnDisconnected = [];          // TODO determine type -> [SGMessageClientManager, (SGMessagingClient, clientId)]
    OnControl = [];               // TODO determine type -> [SGMessageClientManager, (SGMessagingClient, clientId, channel, command)]
    OnSubscribed = [];            // TODO determine type -> [SGMessageClientManager, (SGMessagingClient, msg)]
    OnUnsubscribed = [];          // TODO determine type -> [SGMessageClientManager, (SGMessagingClient, clientId)]
    OnSgrtMessage = []            // TODO determine type -> [SGMessageClientManager, (SGMessagingClient, msg)]
    OnTextMessage = []            // TODO determine type -> [SGMessageClientManager, (SGMessagingClient, msg)]
    OnBinaryMessage = [];         // TODO determine type -> [SGMessageClientManager, (SGMessagingClient, msg)]
    OnError = [];                 // TODO determine type -> [SGMessageClientManager, (SGMessagingClient, sgem)]
    OnTimeResponse = [];          // TODO determine type -> [SGMessageClientManager, (SGMessagingClient, sgtrm)]
    OnTimeErrorResponse = [];     // TODO determine type
    OnNetworkLBResponse = [];     // TODO determine type
    OnIncrementCredit = [];       // TODO determine type
    networkLBRspQueue: SGNetworkLBMessage[] = [];       // TODO determine type

    _AdjustedDeltaTicksResult: AutoResetEvent;

    bLocalErrorTicksSet = false;
    bFirstCheck = false;
    private _FirstCheck = false;
    private _TimeForResponseMS = 0;
    private _BaseAdjustmentMS = 0;
    private _ErrorMS = 0;
    private _ResponseMS = 0;
    Connected = false;
    private _IsFirstCheck = true;
    private _TimeAdjustmentMS = 0;
    private _CalculatedErrorMS = 0;
    private _ErrorCollection = new ErrorCollection();
    private _ProcessTimeMS = 0;
    private _DeltaMS = 0;
    public get IsRTSTimeValid(): boolean { return this._DeltaMS != 0 }

    private _NetworkClockIntervalId: number; // id from setTimeout

    // byte total information fields
    databytesSinceLastConnection(clientId: number): number
    {
        // Validate input
        if ((typeof clientId == 'number'))
        {
            if (SGMessagingClientInstance.has(clientId))
            {
                const client = SGMessagingClientInstance.get(clientId);

                return client.databytesSinceLastConnection;
            }
        }
    }

    databytesInPrevHour(clientId: number): number
    {
        // Validate input
        if ((typeof clientId == 'number'))
        {
            if (SGMessagingClientInstance.has(clientId))
            {
                const client = SGMessagingClientInstance.get(clientId);

                return client.databytesInPrevHour;
            }
        }
    }

    databytesInPrevMin(clientId: number): number
    {
        // Validate input
        if ((typeof clientId == 'number'))
        {
            if (SGMessagingClientInstance.has(clientId))
            {
                const client = SGMessagingClientInstance.get(clientId);

                return client.databytesInPrevMin;
            }
        }
    }

    databytesInPrevSec(clientId: number): number
    {
        // Validate input
        if ((typeof clientId == 'number'))
        {
            if (SGMessagingClientInstance.has(clientId))
            {
                const client = SGMessagingClientInstance.get(clientId);

                return client.databytesInPrevSec;
            }
        }
    }

    databytesOutSinceLastConnection(clientId: number): number
    {
        // Validate input
        if ((typeof clientId == 'number'))
        {
            if (SGMessagingClientInstance.has(clientId))
            {
                const client = SGMessagingClientInstance.get(clientId);

                return client.databytesOutSinceLastConnection;
            }
        }
    }

    databytesOutInPrevHour(clientId: number): number
    {
        // Validate input
        if ((typeof clientId == 'number'))
        {
            if (SGMessagingClientInstance.has(clientId))
            {
                const client = SGMessagingClientInstance.get(clientId);

                return client.databytesOutInPrevHour;
            }
        }
    }

    databytesOutInPrevMin(clientId: number): number
    {
        // Validate input
        if ((typeof clientId == 'number'))
        {
            if (SGMessagingClientInstance.has(clientId))
            {
                const client = SGMessagingClientInstance.get(clientId);

                return client.databytesOutInPrevMin;
            }
        }
    }

    databytesOutInPrevSec(clientId: number): number
    {
        // Validate input
        if ((typeof clientId == 'number'))
        {
            if (SGMessagingClientInstance.has(clientId))
            {
                const client = SGMessagingClientInstance.get(clientId);

                return client.databytesOutInPrevSec;
            }
        }
    }

    constructor(url: string){
        this.LOCAL_DELTA_TIME_CALL = "sgmsgclient_deltatimereq";
        let modifiedUrl = url.split('?')[0];
        if (!modifiedUrl.endsWith('/'))
            modifiedUrl += "/";
        // TODO: This may need to be changed to a real JoinableTaskFactory instead of null

        this.sgmc = this;
        this.timeRspQueue = [];
        // TODO: This will not work with multiple concurrent requests.
///        timeRspAvailable = new TaskCompletionSource < bool > ();

        this.timeErrRspQueue = [];
        this.OnConnected = [];
        this.OnDisconnected = [];
        this.OnSubscribed = [];
        this.OnUnsubscribed = [];
        this.OnSgrtMessage = [];
        this.OnTextMessage = [];
        this.OnBinaryMessage = [];
        this.OnError = [];
        this.OnTimeResponse = [];
        this.OnTimeErrorResponse = [];
        this.OnNetworkLBResponse = [];
        this.networkLBRspQueue = [];
        // TODO: This will not work with multiple concurrent requests.
///        timeErrRspAvailable = new TaskCompletionSource < bool > ();

        // Add the time error response callback for use locally.
        this.OnTimeErrorResponse.push([this, this.GotTimeErrorResponse]);

        // TODO: This will not work with multiple concurrent requests.
        //        networkLBRspAvailable = new TaskCompletionSource < bool > ();
        this._AdjustedDeltaTicksResult = new AutoResetEvent();

        this.bLocalErrorTicksSet = false;
        this.bFirstCheck = false;
        this._FirstCheck = true;
        this._TimeForResponseMS = 0;
        this._BaseAdjustmentMS = 0;
        this._ErrorMS = 0;
        this._ResponseMS = 0;
        this.Connected = false;
        this._IsFirstCheck = true;
        this._TimeAdjustmentMS = 0;
        this._CalculatedErrorMS = 0;
        this._ErrorCollection = new ErrorCollection();
        this._ProcessTimeMS = 0;
        this._DeltaMS = 0;
    }

    /// <summary>
    /// Creates client instance
    /// </summary>
    /// <returns>Client identifier as <typeparamref name="int"/> to use for calls into the client</returns>
    CreateClient() {
        const newClient = new SGMessagingClientF();
        if (newClient == null) {
            return 999;
        }
        SGMessagingClientInstance.set(NextIndex, newClient);
        if (SGMessagingClientInstance.get(NextIndex) == null) {
            return 998;
        }
        const curIndex = NextIndex++;

        // Initialize the internal client properties
        newClient.createClient(curIndex, this.sgmc);
        newClient.onConnected = clientOnConnected;
        newClient.onDisconnected = clientOnDisconnected;
        newClient.onControl = clientOnControl;

        return curIndex;
    }

    /// <summary>
    /// Set the message server URL or IP address
    /// </summary>
    /// <param name="clientId">Client identifier returned from the <see cref="CreateClient"/> call</param>
    /// <param name="url">URL or IP address of the message server to connect to</param>
    /// <returns>None</returns>
    async SetClusterUrl(clientId: number, url: string) {
        // Validate input
        if ((typeof clientId == 'number') && (typeof url == 'string')) {
            // Find client
            if (SGMessagingClientInstance.has(clientId)) {
                const client = SGMessagingClientInstance.get(clientId);

                // Set cluster URL string
                client.setClusterUrl(url);
            }
        }
    }

    /// <summary>
    /// Set the use SSL flag
    /// </summary>
    /// <param name="clientId">Client identifier returned from the <see cref="CreateClient"/> call</param>
    /// <param name="useSsl">True if SSL is used, False if not</param>
    /// <returns>None</returns>
    async SetUseSsl(clientId: number, useSsl: boolean) {
        // Validate input
        if ((typeof clientId == 'number') && (typeof useSsl == 'boolean')) {
            // Find client
            if (SGMessagingClientInstance.has(clientId)) {
                const client = SGMessagingClientInstance.get(clientId);

                client.setUseSsl(useSsl);
            }
        }
    }

    /// <summary>
    /// Starts the client connection process
    /// </summary>
    /// <param name="clientId">Client identifier returned from the <see cref="CreateClient"/> call</param>
    /// <returns>True if process started</returns>
    async Connect(clientId: number) {
        // Validate input
        if ((typeof clientId == 'number')) {
            // Find client
            if (SGMessagingClientInstance.has(clientId)) {
                const client = SGMessagingClientInstance.get(clientId);

                // TODO: The apikey and token need to be provided in the future.
                client.connect(0, 0);

                return true;
            }
        }

        return false;
    }

    /// <summary>
    /// Starts the client disconnection process
    /// </summary>
    /// <param name="clientId">Client identifier returned from the <see cref="CreateClient"/> call</param>
    /// <returns>True if process started</returns>
    Disconnect(clientId: number) {
        // Validate input
        if ((typeof clientId == 'number')) {
            // Find client
            if (SGMessagingClientInstance.has(clientId)) {
                const client = SGMessagingClientInstance.get(clientId);

                //console.log("Disonnecting");
                client.disconnect();
                //console.log("finished disconnecting");

                return true;
            }
        }

        return false;
    }

    /// <summary>
    /// Subscribes to a message channel
    /// </summary>
    /// <param name="clientId">Client identifier returned from the <see cref="CreateClient"/> call</param>
    /// <param name="channel">Name of the channel to subscribe to for messages</param>
    /// <param name="resubOnReconnect">True if automatic subscription should take place after a reconnection</param>
    /// <returns>True if the subscribe message was sent</returns>
    Subscribe(clientId: number, channel: string, resubOnReconnect: boolean, metadataChannel: string, replicateChannel: string, updateChannels: string[], audioChannelFlag: boolean, videoChannelFlag: boolean, ownedFlag: boolean, privateFlag: boolean, channelSendOnlyFlag: boolean) {
        // Validate input
        if ((typeof clientId == 'number') && (typeof channel == 'string') && (typeof resubOnReconnect == 'boolean') &&
            ((audioChannelFlag == undefined) || (videoChannelFlag == undefined) || !(audioChannelFlag && videoChannelFlag)))    // Can't be both an audio and a video channel
        {
            // Find client
            if (SGMessagingClientInstance.has(clientId)) {
                const client = SGMessagingClientInstance.get(clientId);

                client.subscribe(channel, resubOnReconnect, onTextMessage, onBinaryMessage, metadataChannel, replicateChannel, updateChannels, audioChannelFlag, videoChannelFlag, ownedFlag, privateFlag, channelSendOnlyFlag);

                return true;
            }
        }

        return false;
    }

    /// <summary>
    /// Unsubscribes from a message channel
    /// </summary>
    /// <param name="clientId">Client identifier returned from the <see cref="CreateClient"/> call</param>
    /// <param name="channel">Name of the channel to unsubscribe from</param>
    /// <returns>True if the unsubscribe message was sent</returns>
    Unsubscribe(clientId: number, channel: string) {
        // Validate input
        if ((typeof clientId == 'number') && (typeof channel == 'string')) {
            // Find client
            if (SGMessagingClientInstance.has(clientId)) {
                const client = SGMessagingClientInstance.get(clientId);

                client.unsubscribe(channel);

                return true;
            }
        }

        return false;
    }

    async SendAuthenticate(clientId: number, channel: string, token: string) {
        // Validate input
        if ((typeof clientId == 'number') && (typeof channel == 'string') && (typeof token == 'string')) {
            // Find client
            if (SGMessagingClientInstance.has(clientId)) {
                const client = SGMessagingClientInstance.get(clientId);

                await client.sendAuthenticate(channel, token);

                return true;
            }
        }

        return false;
    }

    /// <summary>
    /// Increments credits on the RTS
    /// </summary>
    /// <param name="clientId">Client identifier returned from the <see cref="CreateClient"/> call</param>
    /// <param name="credits">Number of credits to send</param>
    /// <returns>True if the increment credits message was sent</returns>
    SendIncrementCredit(clientId: number, credits: number)
    {
        // Validate input
        if (typeof clientId == "number" && typeof credits == "number")
        {
            // Find client
            if (SGMessagingClientInstance.has(clientId))
            {
                const client = SGMessagingClientInstance.get(clientId);
                client.sendIncrementCredit(credits);
                return true;
            }
        }
        return false;
    }
    /// <summary>
    /// Pauses incoming messages from the specified channel
    /// </summary>
    /// <param name="clientId">Client identifier returned from the <see cref="CreateClient"/> call</param>
    /// <param name="channel">Channel name to pause messages from</param>
    /// <returns>True if the message was sent</returns>
    SendChannelCommandPause(clientId: number, channel: string)
    {
        // Validate input
        if (typeof clientId == "number" && typeof channel == "string")
        {
            // Find client
            if (SGMessagingClientInstance.has(clientId))
            {
                const client = SGMessagingClientInstance.get(clientId);
                return client.sendChannelCommandPause(channel);
            }
        }
        return false;
    }
    /// <summary>
    /// Resumes incoming messages from the specified channel
    /// </summary>
    /// <param name="clientId">Client identifier returned from the <see cref="CreateClient"/> call</param>
    /// <param name="channel">Channel name to resume messages from</param>
    /// <returns>True if the message was sent</returns>
    SendChannelCommandResume(clientId: number, channel: string)
    {
        // Validate input
        if (typeof clientId == "number" && typeof channel == "string")
        {
            // Find client
            if (SGMessagingClientInstance.has(clientId))
            {
                const client = SGMessagingClientInstance.get(clientId);
                return client.sendChannelCommandResume(channel);
            }
        }
        return false;
    }

    /// <summary>
    /// Sends an SG-RT message
    /// </summary>
    /// <param name="clientId">Client identifier returned from the <see cref="CreateClient"/> call</param>
    /// <param name="sgrtMsg">SG-RT message structure to send</param>
    /// <returns>True if message sent</returns>
    // TODO SERIALIZE
    SendSGRT(clientId: number, sgrtMsg: SG_RT_Message) {
        let type = sgrtMsg.PayloadType;
        if (type.length > 4)
        {
            type = type.substring(0, 4);
        }
        else if (type.length < 4)
        {
            type = type + "....".substring(0, 4 - type.length);
        }

        let ver = sgrtMsg.PayloadVersion;
        if (ver.length > 4) {
            ver = ver.substring(0, 4);
        }
        else if (ver.length < 4)
        {
            ver = ver + "....".substring(0, 4 - ver.length);
        }

        //long globalTicks = GlobalTimelineTicks();
        //long globalTicks = GlobalTimelineTicksImmediate();
        let pt = `${sgrtMsg.PresentationTime}`;
        if (pt.length > 128)
        {
            pt = pt.substring(0, 128);
        }
        else if (pt.length < 128)
        {
            const whiteSpace = " ";
            const totalSpace = whiteSpace.repeat(128 - pt.length);
            pt = pt + totalSpace;
        }

        const msg = `${type}${ver}${pt}${sgrtMsg.Data}`;
        return clientSend(clientId, sgrtMsg.Channel, msg);
    }

    /// <summary>
    /// Sends a Text message
    /// </summary>
    /// <param name="clientId">Client identifier returned from the <see cref="CreateClient"/> call</param>
    /// <param name="textMsg">Text message structure to send</param>
    /// <returns>True if message sent</returns>
    // TODO: Resolve issues with usage of this, we only pass in types SGTextMessage but this code won't work if Msg is type SGTextMessage
    Send(clientId: number, Msg: SG_RT_Message | SGTextMessage)
    {
        if (Msg instanceof SGTextMessage) // Previously was Msg.IsText
            return clientSend(clientId, Msg.Channel, Msg.Message);
        else
            return clientSendRaw(clientId, Msg.Channel, Msg.RawData);
    }

    RequestTime(clientId: number, channel: string) {
        // Validate input
        if ((typeof clientId == 'number') && (typeof channel == 'string')) {
            // Find client
            if (SGMessagingClientInstance.has(clientId)) {
                const client = SGMessagingClientInstance.get(clientId);

                return client.requestTime(channel);
            }
        }

        return false;
    }

    // here, ticks is a string
    RequestTimeError(clientId: number, channel: string, ticks: number) {
        // Validate input
        if ((typeof clientId == 'number') && (typeof channel == 'string')) {
            // Find client
            if (SGMessagingClientInstance.has(clientId)) {
                const client = SGMessagingClientInstance.get(clientId);

                //client.sgmc.invokeMethodAsync('JSDebugOutput', client.clientId, "clientRequestTimeError Sending " + time + " on <" + channel + ">");
                return client.requestTimeError(channel, ticks);
            }
        }

        return false;
    }

    SetSendQueueLimit(clientId: number, limit: number) {
        // Validate input
        if ((typeof clientId == 'number') && (typeof limit == 'number')) {
            // Find client
            if (SGMessagingClientInstance.has(clientId)) {
                const client = SGMessagingClientInstance.get(clientId);

                //client.sgmc.invokeMethodAsync('JSDebugOutput', client.clientId, "clientSetSendQueueLimit Sending <" + limit + ">");
                return client.setSendQueueLimit(limit);
            }
        }

        return false;
    }

    RequestConnectionMode(clientId: number, newConnectionMode: number) {
        // Validate input
        if ((typeof clientId == 'number') && (typeof newConnectionMode == 'number')) {
            // Find client
            if (SGMessagingClientInstance.has(clientId)) {
                const client = SGMessagingClientInstance.get(clientId);

                //client.sgmc.invokeMethodAsync('JSDebugOutput', client.clientId, "clientRequestConnectionMode Sending <" + newConnectionMode + ">");
                return client.requestConnectionMode(newConnectionMode);
            }
        }

        return false;
    }


    RequestNetworkLB(clientId: number, localChannel: string, remoteChannel: string, testSize: number) {
        // TODO:  Setup the entire networkLB structure here so that the only thing the JS code needs to do is send the serialized info.
        // Validate input
        if ((typeof clientId == 'number') && (typeof localChannel == 'string') && (typeof remoteChannel == 'string') && (typeof testSize == 'number')) {
            // Find client
            if (SGMessagingClientInstance.has(clientId)) {
                const client = SGMessagingClientInstance.get(clientId);

                //client.sgmc.invokeMethodAsync('JSDebugOutput', client.clientId, "clientRequestTimeError Sending " + time + " on <" + channel + ">");
                return client.requestNetworkLB(localChannel, remoteChannel, testSize);
            }
        }

        return false;
    }

/*
    public EventHandler<SGSubscriptionMessage>?OnSubscribed;
    public EventHandler<SGSubscriptionMessage>?OnUnsubscribed;
    public EventHandler<SG_RT_Message>?OnSgrtMessage;
    public EventHandler<SGTextMessage>?OnTextMessage;
    public EventHandler<SGBinaryMessage>?OnBinaryMessage;
    public EventHandler<SGErrorMessage>?OnError;
    public EventHandler<SGTimeResponseMessage>?OnTimeResponse;
    public EventHandler<SGTimeErrorResponseMessage>?OnTimeErrorResponse;
    public EventHandler<SGNetworkLBMessage>?OnNetworkLBResponse;
*/


    RTSTime(clientId: number)
    {
        const currentTimeMS = this.getCurrentMS();
        //console.log(`RTSTime = ${currentTimeMS + this._DeltaMS}  currentTimeMS = ${currentTimeMS}  _DeltaMS = ${this._DeltaMS}`);
        return currentTimeMS + this._DeltaMS;
    }

    RTSTimeLatched( clientId: number )
    {
        return this.bLocalErrorTicksSet;
    }

    getCurrentMS() {
        //const epochTicks = 621355968000000000; // big int
        //let now = performance.timeOrigin + performance.now();
        //let ticks = (now * 10000) + epochTicks;
        //const epochTicks = 62135596800000; // big int
        //let now = ~~((performance.timeOrigin || performance.timing.navigationStart) + performance.now());
        //let MS = (now * 1) + epochTicks;

        //console.log(`getCurrentTicks: ${epochTicks} ${now} ${ticks}; ${performance.timing.navigationStart} ${performance.now()}`);

        return LocalClock.now();
    }

    updateClock()
    {
        LocalClock.updateOffset();
    }

    getCurrentTicks() {
        const now = (performance.timeOrigin || performance.timing.navigationStart) + performance.now();
        return SGUtility.UtcMSToCSharpTicks(now);
    }

    GotTimeErrorResponse(sender: SGMessagingClient, sgterm: SGTimeErrorResponseMessage) {
        // TODO: Verify the client id's match
        sender._TimeForResponseMS = LocalClock.now() - sender._TimeForResponseMS;  // Was ticks (so *10000) but now ms
        sender._ErrorMS = sgterm.ErrorTicks / 10000;
        sender._ResponseMS = sgterm.ResponseTicks / 10000;

        //console.log(`got response e ${sender._ErrorMS}  r ${sender._ResponseMS}  timeforresponse ${sender._TimeForResponseMS}  baseadjust ${this._TimeAdjustmentMS}`);

        sender._AdjustedDeltaTicksResult.set();
    }

    async getTimeErrorAsync(clientId = 0) {
        if (!this.Connected) {
            // This will typically happen at the beginning when waiting for the rts connection to be made.
            //console.warn('Network clock failed to update. Messaging client not connected.');
            this._NetworkClockIntervalId = setTimeout(this.getTimeErrorAsync.bind(this), 100);
            return;
        }
        let count = 0;
        let limit = 0;
        if (!this._FirstCheck) {
            this._FirstCheck = true;
            limit = 30;
        }
        const OUTLIER_LIMIT = 0.5; // ms allowed error.
        do {
            this._TimeForResponseMS = this.getCurrentMS();
            const requestTicks = SGUtility.UtcMSToCSharpTicks(this._TimeForResponseMS + (this._TimeAdjustmentMS || 0)); // sometimes it became NaN, so needed debugging
            if (this.RequestTimeError(clientId, `GTEA${clientId}`, requestTicks)) {
                // Wait for the results from the request
                await this._AdjustedDeltaTicksResult.waitOne();
                if (this._IsFirstCheck) {
                    this._TimeAdjustmentMS = this._ErrorMS;
                    this._IsFirstCheck = false;
                }
                else {
                    if (this.bLocalErrorTicksSet) {
                        if (this._ErrorMS > OUTLIER_LIMIT) {
                            this._ErrorMS = OUTLIER_LIMIT;
                        }
                        else if (this._ErrorMS < -OUTLIER_LIMIT) {
                            this._ErrorMS = -OUTLIER_LIMIT;
                        }
                    }

                    if (isNaN(this._TimeAdjustmentMS))
                    {
                        // This should never happen, track it
                        //console.error("_TimeAdjustMS == NaN");
                        this._TimeAdjustmentMS = this._ErrorMS;
                    }
                    else
                        this._TimeAdjustmentMS += this._ErrorMS;

                    const nextDeltaMS = this._TimeAdjustmentMS - this._ResponseMS - (this._TimeForResponseMS / 2);
                    if (this._DeltaMS != 0)
                    {
                        if (nextDeltaMS > this._DeltaMS)
                        {
                            if ((limit != 30) && ((nextDeltaMS - this._DeltaMS) > OUTLIER_LIMIT))
                            {
                                this._DeltaMS += OUTLIER_LIMIT;
                            }
                            else
                            {
                                this._DeltaMS = nextDeltaMS;
                            }
                        }
                        else
                        {
                            if ((limit != 30) && ((nextDeltaMS - this._DeltaMS) < -OUTLIER_LIMIT))
                            {
                                this._DeltaMS -= OUTLIER_LIMIT;
                            }
                            else
                            {
                                this._DeltaMS = nextDeltaMS;
                            }
                        }
                    }
                    else
                    {
                        // This else clause was added since there are cases where the limit on checks was hit before any succeeded.
                        this._DeltaMS = nextDeltaMS;
                    }
                }


/* what it was
                if (this._IsFirstCheck) {

                    this._TimeAdjustmentMS = this._CalculatedErrorMS;
                    this._IsFirstCheck = false;
                }
                else {
                    // Clamp the value to get rid of outliers. Using the running avg as a clamp rFinaather 
                    // than -5000 to 5000 like in C#.
                    this._ErrorCollection.add(this._CalculatedErrorMS);
                    var errorAvg = this._ErrorCollection.getAverage();
                    this._CalculatedErrorMS = MathExtensions.clamp(this._CalculatedErrorMS, -errorAvg, errorAvg);
                    // Update time adjusment
                    this._TimeAdjustmentMS += this._CalculatedErrorMS;
                    // Clamp the delta to get rid of outliers
                    let nextDeltaMS = this._TimeAdjustmentMS - this._ProcessTimeMS - (this._TimeForResponseMS / 2 );
                    if (nextDeltaMS > this._DeltaMS) {
                        if ((bErrorTicksSet) && (nextDeltaMS - this._DeltaMS > 1)) {
                            this._DeltaMS += 5000;// 1; //bigint - was 5000 as a BigInt of ticks.  Now just 1MS
                        }
                        else {
                            this._DeltaMS = nextDeltaMS;
                        }
                    }
                    else {
                        if ((bErrorTicksSet) && (nextDeltaMS - this._DeltaMS < -1)) {
                            this._DeltaMS -= 5000;//; //bigint - was 5000 as a BigInt of ticks.  Now just 1MS
                        }
                        else {
                            this._DeltaMS = nextDeltaMS;
                        }
                    }
                }
*/
            }

            //console.log(`_DeltaMS = ${this._DeltaMS}  _TimeAdjustmentMS = ${this._TimeAdjustmentMS}  _ReponseMS = ${this._ResponseMS}  _TimeForResponseMS = ${this._TimeForResponseMS}`);
            count++;
            if (!this.bLocalErrorTicksSet) {
                /*
                if (!useClamp && (count > limit)) {
                    useClamp = true;
                }
                if (count > limit * 2) {
                    bErrorTicksSet = true;
                }
                */
                if (count > limit)
                {
                    this.bLocalErrorTicksSet = true;
                }
            }
        } while (!this.bLocalErrorTicksSet);

        this.bLocalErrorTicksSet = true;
        this._NetworkClockIntervalId = setTimeout(this.getTimeErrorAsync.bind(this), 1000, clientId);
    }
}

class ErrorCollection
{
    private _ErrorArray: number[];
    private _MaxSize: number;
    private _RunningAvg: number;

    constructor(maxSize = 30) {
        this._ErrorArray = [];
        this._MaxSize = maxSize;
        this._RunningAvg = 0;
    }

    add(value: number) {
        if (this._ErrorArray.length >= this._MaxSize) {
            this._ErrorArray.shift();
        }
        value = value < 0 ? -value : value; // Take the absolute value
        if (this._ErrorArray.length >= this._MaxSize) {
            const avg = this.getAverage();
            const maxClamp = Number(avg) * 1.3; // No specific reason for 1.3, but might want to come back and perform a proper standard deviation calculation
            if (value > maxClamp) {
                value = (maxClamp);
            }
        }
        this._ErrorArray.push(value);
    }

    getAverage() {
        if (this._ErrorArray.length === 0)
            return 0;
        return this._ErrorArray.reduce((a, b) => a + b, 0) / (this._ErrorArray.length);
    }
}
export class MathExtensions {
    static clamp(value: number, min: number, max: number) {
        if (value > max) {
            value = max;
        }
        else if (value < min) {
            value = min;
        }
        return value;
    }
}

//MathExtensions.clamp = function (value, min, max) {
//    if (value > max) {
//        value = max;
//    }
//    else if (value < min) {
//        value = min;
//    }
//    return value;
//};

export function createClient(sgmc: SGMessagingClient) {
    const newClient = new SGMessagingClientF();
    if (newClient == null) {
        return 999;
    }
    SGMessagingClientInstance.set(NextIndex, newClient);
    if (SGMessagingClientInstance.get(NextIndex) == null) {
        return 998;
    }
    const curIndex = NextIndex++;

    // Initialize the internal client properties
    newClient.createClient(curIndex, sgmc);
    newClient.onConnected = clientOnConnected;
    newClient.onDisconnected = clientOnDisconnected;

    return curIndex;
}

//export async function clientSetClusterUrl(clientId: string, url: string) {
//    // Validate input
//    if ((typeof clientId == 'number') && (typeof url == 'string')) {
//        // Find client
//        if (SGMessagingClientInstance.has(clientId)) {
//            const client = SGMessagingClientInstance.get(clientId);

//            // Set cluster URL string
//            client.setClusterUrl(url);
//        }
//    }
//}

//export async function clientSetUseSsl(clientId: number, useSsl: boolean) {
//    // Validate input
//    if ((typeof clientId == 'number') && (typeof useSsl == 'boolean')) {
//        // Find client
//        if (SGMessagingClientInstance.has(clientId)) {
//            const client = SGMessagingClientInstance.get(clientId);

//            client.setUseSsl(useSsl);
//        }
//    }
//}

//export function clientConnect(clientId: number) {
//    // Validate input
//    if ((typeof clientId == 'number')) {
//        // Find client
//        if (SGMessagingClientInstance.has(clientId)) {
//            const client = SGMessagingClientInstance.get(clientId);

//            client.connect(0, 0);

//            return true;
//        }
//    }

//    return false;
//}

//export function clientDisconnect(clientId: number) {
//    // Validate input
//    if ((typeof clientId == 'number')) {
//        // Find client
//        if (SGMessagingClientInstance.has(clientId)) {
//            const client = SGMessagingClientInstance.get(clientId);

//            //console.log("Disonnecting");
//            client.disconnect();
//            //console.log("finished disconnecting");

//            return true;
//        }
//    }

//    return false;
//}

//export function clientSubscribe(clientId: number, channel: string, resubOnReconnect: boolean) {
//    // Validate input
//    if ((typeof clientId == 'number') && (typeof channel == 'string') && (typeof resubOnReconnect == 'boolean')) {
//        // Find client
//        if (SGMessagingClientInstance.has(clientId)) {
//            const client = SGMessagingClientInstance.get(clientId);

//            client.subscribe(channel, resubOnReconnect, onTextMessage, onBinaryMessage);

//            return true;
//        }
//    }

//    return false;
//}

//export function clientUnsubscribe(clientId: number, channel: string) {
//    // Validate input
//    if ((typeof clientId == 'number') && (typeof channel == 'string')) {
//        // Find client
//        if (SGMessagingClientInstance.has(clientId)) {
//            const client = SGMessagingClientInstance.get(clientId);

//            client.unsubscribe(channel);

//            return true;
//        }
//    }

//    return false;
//}

//export async function clientSendAuthenticate(clientId: string, channel: string, token: string) {
//    // Validate input
//    if ((typeof clientId == 'number') && (typeof channel == 'string') && (typeof token == 'string')) {
//        // Find client
//        if (SGMessagingClientInstance.has(clientId)) {
//            const client = SGMessagingClientInstance.get(clientId);

//            await client.sendAuthenticate(channel, token);

//            return true;
//        }
//    }

//    return false;
//}

export function clientSend(clientId: number, channel: string, msg: string) {
    // Validate input
    if ((typeof clientId == 'number') && (typeof channel == 'string')) {
        // Find client
        if (SGMessagingClientInstance.has(clientId)) {
            const client = SGMessagingClientInstance.get(clientId);

            client.send(channel, msg);

            return true;
        }
    }

    return false;
}

export function clientSendRaw(clientId: number, channel: string, msg: Uint8Array<ArrayBufferLike>)
{
    // Validate input
    if ((typeof clientId == 'number') && (typeof channel == 'string')) {
        // Find client
        if (SGMessagingClientInstance.has(clientId)) {
            const client = SGMessagingClientInstance.get(clientId);

            client.sendRaw(channel, msg);

            return true;
        }
    }

    return false;
}

//export function clientRequestTime(clientId: number, channel: string) {
//    // Validate input
//    if ((typeof clientId == 'number') && (typeof channel == 'string')) {
//        // Find client
//        if (SGMessagingClientInstance.has(clientId)) {
//            const client = SGMessagingClientInstance.get(clientId);

//            return client.requestTime(channel);
//        }
//    }

//    return false;
//}

//export function clientRequestTimeError(clientId: number, channel: string, time: number) {
//    // Validate input
//    if ((typeof clientId == 'number') && (typeof channel == 'string')) {
//        // Find client
//        if (SGMessagingClientInstance.has(clientId)) {
//            const client = SGMessagingClientInstance.get(clientId);

//            //client.sgmc.invokeMethodAsync('JSDebugOutput', client.clientId, "clientRequestTimeError Sending " + time + " on <" + channel + ">");
//            return client.requestTimeError(channel, time);
//        }
//    }

//    return false;
//}

//export function clientSetSendQueueLimit(clientId: number, limit: number) {
//    // Validate input
//    if ((typeof clientId == 'number') && (typeof limit == 'number')) {
//        // Find client
//        if (SGMessagingClientInstance.has(clientId)) {
//            const client = SGMessagingClientInstance.get(clientId);

//            //client.sgmc.invokeMethodAsync('JSDebugOutput', client.clientId, "clientSetSendQueueLimit Sending <" + limit + ">");
//            return client.setSendQueueLimit(limit);
//        }
//    }

//    return false;
//}

//export function clientRequestConnectionMode(clientId: number, newConnectionMode: number) {
//    // Validate input
//    if ((typeof clientId == 'number') && (typeof newConnectionMode == 'number')) {
//        // Find client
//        if (SGMessagingClientInstance.has(clientId)) {
//            const client = SGMessagingClientInstance.get(clientId);

//            //client.sgmc.invokeMethodAsync('JSDebugOutput', client.clientId, "clientRequestConnectionMode Sending <" + newConnectionMode + ">");
//            return client.requestConnectionMode(newConnectionMode);
//        }
//    }

//    return false;
//}

//function clientRequestNetworkLB(clientId: number, localChannel: string, remoteChannel: string, testSize: number) {
//    // Validate input
//    if ((typeof clientId == 'number') && (typeof localChannel == 'string') && (typeof remoteChannel == 'string') && (typeof testSize == 'number')) {
//        // Find client
//        if (SGMessagingClientInstance.has(clientId)) {
//            const client = SGMessagingClientInstance.get(clientId);

//            //client.sgmc.invokeMethodAsync('JSDebugOutput', client.clientId, "clientRequestTimeError Sending " + time + " on <" + channel + ">");
//            return client.requestNetworkLB(localChannel, remoteChannel, testSize);
//        }
//    }

//    return false;
//}

function onTextMessage(client: SGMessagingClientF, channel: string, msg: string)
{
    //console.log("Sending onTextMessage with msg size " + msg.length);
    ///    client.sgmc.invokeMethod('JSOnTextMessageCallback', client.clientId, channel, msg);

    // Parse message to see if it's an SG-RT type.
    // 135 is the payload type size plus payload version size plus the presentation time field size minus 1.
    if ((msg.length > 135) && (msg.substring(0, 4) == "SGRT"))
    {
        // Found an SGRT formatted message, so return that back to the onSgrtMessage callback
        const payloadVersion = msg.substring(4, 4+4);
        const presentationTime = msg.substring(8, 8+128);
        const data = msg.substring(136, msg.length);
        const message = new SG_RT_Message(client.clientId, channel, "SGRT", payloadVersion, presentationTime, data);
        if (client.sgmc.OnSgrtMessage != null) client.sgmc.OnSgrtMessage.forEach((targ) => targ[1](targ[0], message));
    }
    else
    {
        const textMsg = new SGTextMessage(client.clientId, channel, msg);
        if (client.sgmc.OnTextMessage != null) client.sgmc.OnTextMessage.forEach((targ) => targ[1](targ[0], textMsg));
    }
}

let td: TextDecoder = null;

function onBinaryMessage(client: SGMessagingClientF, channel: string, msg: Uint8Array<ArrayBufferLike>)
{
    //console.log("Sending onBinaryMessage with msg size " + msg.length);
    ///    client.sgmc.invokeMethod('JSOnBinaryMessageCallback', client.clientId, channel, msg);

    // Parse message
    // 135 is the payload type size plus the payload version size plus the presentation time field size minus 1.
    if (!td)
    {
        td = new TextDecoder();
    }
    const msgType = td.decode(msg.slice(0,4));

    // TODO SERIALIZE add branch to handle binary messages of sgrt (high bit is set)
    if ((msg.length > 135) && (msgType == "SGRT"))
    {
        //if (Debugs.stream) console.log("Got SGRT onBinaryMessage");
        // SG-RT format binary message
        const payloadVersion = td.decode(msg.slice(4, 4+4));
        const presentationTime = td.decode(msg.slice(8, 8 + 128));
        let dispatchMsg: SG_RT_Message;
        if (msg.length == 136)
        {
            // Null message data since all fields including presentation time use up the entire byte[] buffer.  Type cast is for specifying which constructor to use.
            dispatchMsg = new SG_RT_Message(client.clientId, channel, "SGRT", payloadVersion, presentationTime, null);
        }
        else
        {
            dispatchMsg = new SG_RT_Message(client.clientId, channel, "SGRT", payloadVersion, presentationTime, msg.slice(136, msg.length));
        }

        if (client.sgmc.OnSgrtMessage != null) client.sgmc.OnSgrtMessage.forEach((targ) => targ[1](targ[0], dispatchMsg));
    }
    else
    {
        //if (IndexCode.ViewerInstance.bOutputToConsole) console.log(`${msgType}`);
        // Unknown message type.  Just send the entire message as a single byte[] buffer
        const binaryMsg = new SGBinaryMessage(client.clientId, channel, msg);
        if (client.sgmc.OnBinaryMessage != null) client.sgmc.OnBinaryMessage.forEach((targ) => targ[1](targ[0], binaryMsg));
    }
}

function clientOnConnected(client: SGMessagingClientF) {
    //    client.sgmc.invokeMethodAsync('JSOnConnectedCallback', client.clientId);
    client.sgmc.Connected = true;
    if (client.sgmc.OnConnected != null) client.sgmc.OnConnected.forEach((targ) => targ[1](targ[0], client.clientId));
}

function clientOnDisconnected(client: SGMessagingClientF) {
    //console.log("sending up disconnected event");
//    client.sgmc.invokeMethodAsync('JSOnDisconnectedCallback', client.clientId);
    if (client.sgmc.OnDisconnected != null) client.sgmc.OnDisconnected.forEach((targ) => targ[1](targ[0], client.clientId));
    client.sgmc.Connected = false;
}

// TODO: Change command to a class in order to extend the types of control messages that can be processed.
function clientOnControl( client: SGMessagingClientF, channel: string, command: string )
{
    if ( client.sgmc.OnControl != null ) client.sgmc.OnControl.forEach( ( targ ) => targ[ 1 ]( targ[ 0 ], client.clientId, channel, command ) );
}

function clientOnSubscribed(client: SGMessagingClientF, channel: string) {
    //console.log("sending up subscribed event");
//    client.sgmc.invokeMethodAsync('JSOnSubscribedCallback', client.clientId, channel);
    if (client.sgmc.OnSubscribed != null) client.sgmc.OnSubscribed.forEach((targ) => targ[1](targ[0], client.clientId, channel));
}

function clientOnUnsubscribed(client: SGMessagingClientF, channel: string) {
    //console.log("sending up unsubscribed event");
//    client.sgmc.invokeMethodAsync('JSOnUnsubscribedCallback', client.clientId, channel);
    if (client.sgmc.OnUnsubscribed != null) client.sgmc.OnUnsubscribed.forEach((targ) => targ[1](targ[0], client.clientId, channel));
}

function clientOnError(client: SGMessagingClientF, event: Event) {
//    client.sgmc.invokeMethodAsync('JSOnErrorCallback', client.clientId, event);
    if (client.sgmc.OnError != null) client.sgmc.OnError.forEach((targ) => targ[1](targ[0], client.clientId, event));
}

function clientOnRequestTimeResponse(client: SGMessagingClientF, channel: string, msg: string) {
    //client.sgmc.invokeMethodAsync('JSDebugOutput', client.clientId, "clientOnRequestTimeResponse Sending back " + msg);

    const timeRsp = JSOnRequestTimeCallback( client.clientId, channel, msg);
    if (client.sgmc.OnTimeResponse != null) client.sgmc.OnTimeResponse.forEach((targ) => targ[1](targ[0], client.clientId, channel, msg));
}

function clientOnRequestTimeErrorResponse(client: SGMessagingClientF, channel: string, msg: string) {
    //client.sgmc.invokeMethodAsync('JSDebugOutput', client.clientId, "clientOnRequestTimeResponse Sending back " + msg);

    const TimeErrorResp = JSOnRequestTimeErrorCallback(client.clientId, channel, msg);
    //client.sgmc._ProcessTimeMS = ~~(TimeErrorResp.ResponseTicks / 10000);
    //client.sgmc._CalculatedErrorMS = ~~(TimeErrorResp.ErrorTicks / 10000);
    
    if (client.sgmc.OnTimeErrorResponse != null) client.sgmc.OnTimeErrorResponse.forEach((targ) => targ[1](targ[0], TimeErrorResp));
}

function clientOnIncrementCredit(client, channel, msg)
{
    const credits = parseInt(msg);
    if (client.sgmc.OnIncrementCredit != null) client.sgmc.OnIncrementCredit.forEach((targ) => targ[1](targ[0], client.clientId, channel, credits));
}

// TODO - this needs to be fixed.  Currently it can be called from SGMessagingClientF looking for this name to exist in that class as an array of [object, function] pairs.
function clientDebugOutput(client: SGMessagingClientF, event: string)
{
//    client.sgmc.invokeMethodAsync('JSDebugOutput', client.clientId, event);
    if (client.sgmc.clientDebugOutput != null) client.sgmc.clientDebugOutput.forEach((targ) => targ[1](targ[0], client.clientId, event));
}

function JSOnRequestTimeCallback(clientId: number, channel: string, networkTimeTicks: string)
{
    const ticks = parseInt(networkTimeTicks.substring(0, networkTimeTicks.length - 4));

    const timeRspMsg = new SGTimeResponseMessage(clientId, channel, ticks);

    return timeRspMsg;
}

function JSOnRequestTimeErrorCallback(clientId: number, channel: string, errorTime: string)
{
    const times = errorTime.split(':');
    let errorTicks;
    let responseTicks;

    if (times.length != 2) {
        // Badly formatted times.  Don't trust it.  Return max times and exit
        errorTicks = Number.MAX_SAFE_INTEGER;
        responseTicks = Number.MAX_SAFE_INTEGER;
    }
    else {
        //let strTim = times[0].substring(0, times[0].length - 4);
        //errorTicks = parseInt(strTim);
        errorTicks = parseInt(times[0]);
        responseTicks = parseInt(times[1]);
    }

    //Console.WriteLine($"Got JSOnRequestTimeErrorCallback channel <{channel}>  errorTicks <{errorTicks}>  responseTicks <{responseTicks}>");
    const timeErrRspMsg = new SGTimeErrorResponseMessage(clientId, channel, errorTicks, responseTicks);
//    OnTimeErrorResponse(this, timeErrRspMsg);

    return timeErrRspMsg;
}

// TODO: remove later when debugging is not needed
function accessClientProperty(index: number, propertyName: string) {
    if (((typeof index) == 'number') && ((typeof propertyName) == 'string')) {
        if (SGMessagingClientInstance.has(index)) {
            const client = SGMessagingClientInstance.get(index);

            if (client == null) {
                return 'o';
            }
            // TODO: hasOwnProperty has issues being called this way, apparently (see eslint no-prototype-builtins)
            else if (client.hasOwnProperty(propertyName)) {
                return client[propertyName];
            }
            else {
                return 'p';
            }
        }
        else {
            return 'q';
        }
    }
    else {
        return 'r';
    }
}

////////////////////////
// CONSTANTS
////////////////////////

// WebSocket subprotocol for messaging.
// TODO: make these part of a better type.
const SGMessagingWebSocketSubProtocol = "sg-ws-msg.plaintext";
const SGMessageTypeSubscribe = 'S';
const SGMessageTypeSubscribePrefetch = 'P'
const SGMessageTypeUnsubscribe = 'U';
const SGMessageTypeSend = 'M';
const SGMessageTypeSendRaw = 'W';
const SGMessageTypePostEntry = 'D';
const SGMessageTypeRequestEntry = 'R';
const SGMessageTypeRequestTime = 'T';
const SGMessageTypeRequestTimeError = 'E';
const SGMessageTypeSetSendQueueLimit = 'L';
const SGMessageTypeRequestConnectionMode = 'C';
const SGMessageTypeRequestNetworkLB = 'N';
const SGMessageTypeKeepAlive = 'K';
const SGMessageTypeAuth = 'A';
const SGMessageTypeIncrementCredit = "Q";
const SGMessageTypeChannelCommandRequest = 'O';
const SGMessageTypeSubscribeResponse = 's';
const SGMessateTypeSubscribePrefetchResponse = 'p';
const SGMessageTypeUnsubscribeResponse = 'u';
const SGMessageTypeSent = 'm';
const SGMessageTypeSentRaw = 'w';
const SGMessageTypePostEntryResponse = 'd';
const SGMessageTypeRequestEntryResponse = 'r';
const SGMessageTypeRequestTimeResponse = 't';
const SGMessageTypeRequestTimeErrorResponse = 'e';
const SGMessageTypeSetSendQueueLimitResponse = 'l';
const SGMessageTypeRequestNetworkLBResponse = 'n';
const SGMessageTypeRecalibrate = '~';

// RealTimeMessage constants
const MessageType_Text: string = "0";
const SerializingDelimiter: string = "     ";
const DebugChannelPrefix: string = "AppDebug_";

// websocket constants
const ReadyStateConnecting: number = 0;
const ReadyStateOpen: number = 1;
const ReadyStateClosing: number = 2;
const ReadyStateClosed: number = 3;

// TODO: find a useful place for this type
export type SGSubscriptionCallback = (client: SGMessagingClientF, channel: string, message: string) => void;
export type SGSubscriptionCallbackRaw = (client: SGMessagingClientF, channel: string, message: Uint8Array<ArrayBufferLike> | string) => void;

export type SGOnConnectedCallback = (client: SGMessagingClientF) => void;
export type SGOnDisconnectedCallback = (client: SGMessagingClientF) => void;
export type SGOnControlCallback = ( client: SGMessagingClientF, channel: string, command: string ) => void;

class SGMessagingClientFSubscription
{
    channel: string;
    reconnect: boolean;
    cb: SGSubscriptionCallback;
    cbraw: SGSubscriptionCallbackRaw;
    token: string = "";
}

export class SGMessagingClientF {
    // TODO: Put in checks here for required libraries.

    // Note: constants moved out into module scope.  They are private to this file.

    // Private helpers
    private td: TextDecoder = new TextDecoder("ascii");


    ////////////////////////
    // VARIABLES
    ////////////////////////

    // TODO: found during conversion back to a class
    socket: WebSocket;
    clientId: number;
    sgmc: SGMessagingClient;
    private msgServer: string;

    // Hold the keepalive timer interval id.
    keepaliveTimer?: number = null;

    // Holds the state check interval id.
    stateChecker?: number = null;

    // Input Queue and Timer
    InputQueue: Array<MessageEvent> = [];
    InputQueueInterval: number = null;

    // The subscription associative array
    subscriptions: Map<string, SGMessagingClientFSubscription> = new Map();

    connected: boolean = false;
    reconnecting: boolean = false;
    disconnecting: boolean = false;
    initialized: boolean = false;

    // SSL flag to determine whether to use ws:// or wss://
    useSsl: boolean = true;

    testchar = 'j';    // TODO: REMOVE AS THIS IS ONLY FOR TESTING!!!

    // Variables for holding current message state.  TODO: fix the types here
    msgcode?: string = null;
    curDataLength: number = 0;
    prevint8buf?: Uint8Array = null;
    chnlen: number = -1;
    msglen: number = -1;
    channel?: string = null;

    // Users fill in the following property if notification of client connected is needed.
    onConnected?: SGOnConnectedCallback = null;

    // Users fill in the following property if notification of client connected is needed.
    onDisconnected?: SGOnDisconnectedCallback = null;

    // Users fill in the following property if processing of client control messages is needed.
    onControl?: SGOnControlCallback = null;

    binaryMsgCount: number = 1;
    nextMsgCount: number = 1;
    bufferList: Map<number, Uint8Array<ArrayBufferLike>> = new Map();

    // bandwidth information fields and intermediate variables
    private databyteUpdater: number = -1;
    private dbSecondsSinceLastUpdate: number = 0;
    private dbMinutesSinceLastUpdate: number = 0;
    private databytesAtLastSecUpdate: number = 0;
    private databytesAtLastMinUpdate: number = 0;
    private databytesAtLastHourUpdate: number = 0;
    public databytesSinceLastConnection: number = 0;
    public databytesInPrevHour: number = 0;
    public databytesInPrevMin: number = 0;
    public databytesInPrevSec: number = 0;

    private databytesOutAtLastSecUpdate: number = 0;
    private databytesOutAtLastMinUpdate: number = 0;
    private databytesOutAtLastHourUpdate: number = 0;
    public databytesOutSinceLastConnection: number = 0;
    public databytesOutInPrevHour: number = 0;
    public databytesOutInPrevMin: number = 0;
    public databytesOutInPrevSec: number = 0;

    constructor(serverUrl?: string)
    {
        // TODO: BUG The only reason for this constructor having a parameter is because utility.ts calls it with a parameter.  We need to actually use it.
        // Start up the databyte calculation function via setInterval as it needs to fire ~every second.
        this.databyteUpdater = setInterval(this.updateDatabyteTotal.bind(this), 1000);
    }

    private updateDatabyteTotal()
    {
        if (this.connected)
        {
            // Update second totals
            this.databytesInPrevSec = this.databytesSinceLastConnection - this.databytesAtLastSecUpdate;
            this.databytesAtLastSecUpdate = this.databytesSinceLastConnection;
            this.databytesOutInPrevSec = this.databytesOutSinceLastConnection - this.databytesOutAtLastSecUpdate;
            this.databytesOutAtLastSecUpdate = this.databytesOutSinceLastConnection;

            // Update minute totals
            this.dbSecondsSinceLastUpdate++;
            if (this.dbSecondsSinceLastUpdate >= 60)
            {
                this.databytesInPrevMin = this.databytesSinceLastConnection - this.databytesAtLastMinUpdate;
                this.databytesAtLastMinUpdate = this.databytesSinceLastConnection;
                this.databytesOutInPrevMin = this.databytesOutSinceLastConnection - this.databytesOutAtLastMinUpdate;
                this.databytesOutAtLastMinUpdate = this.databytesOutSinceLastConnection;
                this.dbSecondsSinceLastUpdate = 0;
            }

            // Update hour totals
            this.dbMinutesSinceLastUpdate++;
            if (this.dbMinutesSinceLastUpdate >= 60)
            {
                this.databytesInPrevHour = this.databytesSinceLastConnection - this.databytesAtLastHourUpdate;
                this.databytesAtLastHourUpdate = this.databytesSinceLastConnection;
                this.databytesOutInPrevHour = this.databytesOutSinceLastConnection - this.databytesOutAtLastHourUpdate;
                this.databytesOutAtLastHourUpdate = this.databytesOutSinceLastConnection;
                this.dbMinutesSinceLastUpdate = 0;
            }
        }
    }

    ////////////////////////
    // INTERNAL METHODS
    ////////////////////////

    // All messages are string format with fields:
    // message type - char, one of 'S' (subscribe) 'U' (unsubscribe) 'M' (send message) 's' (subscribe response) 'u' (unsubscribe response) 'm' (sent message) 'Q' (increment credit message)
    // bytes in channel field - 3 number chars for length
    // bytes in message field - 4 number chars for length
    // channel name - string of length bytes in channel
    // message - string of length bytes in message
    // TODO: Add in formatting for large (>4KB) messages.
    private SGFormat(type: string, channel: string, msg?: string | Uint8Array | undefined) {
        if (typeof type != 'string') {
            return;
        }

        if (typeof channel != 'string') {
            return;
        }

        if (typeof msg != 'string') {
            if (msg instanceof Uint8Array) {
                const sampleChannel = new Blob([channel]);
                const csize = sampleChannel.size;
                const size = new Blob([msg]).size;
                const msize = size;

                const binmsg = new Uint8Array(csize + msize + 8);

                const cnum = ('000' + csize).slice(-3);
                if (msize > 9999) {
                    binmsg[7] = (msize >> 0) % 256;
                    binmsg[6] = (msize >> 8) % 256;
                    binmsg[5] = (msize >> 16) % 256;
                    binmsg[4] = ((msize >> 24) % 256) | 0x80;
                }
                else {
                    const mnum = ('0000' + msize).slice(-4);
                    binmsg[4] = mnum.charCodeAt(0);
                    binmsg[5] = mnum.charCodeAt(1);
                    binmsg[6] = mnum.charCodeAt(2);
                    binmsg[7] = mnum.charCodeAt(3);
                }

                binmsg[0] = type.charCodeAt(0);
                binmsg[1] = cnum.charCodeAt(0);
                binmsg[2] = cnum.charCodeAt(1);
                binmsg[3] = cnum.charCodeAt(2);

                //for (var i = 0; i < csize; i++)
                //{
                //    binmsg[8 + i] = channel.charCodeAt(i);
                //}
                const utf8Encoder = new TextEncoder();
                const encodedChannel = utf8Encoder.encode(channel);
                binmsg.set(encodedChannel, 8);

                binmsg.set(msg, 8 + csize);

                return binmsg;
            }
            else if (typeof msg != 'undefined') {
                return;
            }

            msg = '';
        }

        // Convert to blob to get exact byte value
        let size = new Blob([channel]).size;
        const csize = size;
        size = new Blob([msg]).size;
        const msize = size;

        const cnum = ('000' + csize).slice(-3);
        const mnum = ('0000' + msize).slice(-4);
        return type + cnum + mnum + channel + msg;
    }

     private keepalive() {
        if (this.connected) {
            if (this.socket.readyState == ReadyStateOpen) {
                //console.log("keepalive: sending");
                // Send the keep-alive
                const msg = this.SGFormat(SGMessageTypeKeepAlive, 'keepAlive');

                if (typeof msg != 'undefined') {
                    // Should always get here.
                    try
                    {
                        this.databytesOutSinceLastConnection += msg.length;
                        this.socket.send(msg);
                    }
                    catch (error) {
                        if (error instanceof DOMException) {
                            if (error.name != "InvalidStateError") {
                                //alert("received uncaught DOMException");
                                //throw error;
                            }
                        }
                    }
                }
            }
        }
        else if ((!this.reconnecting) && (!this.disconnecting)) {
            // Reconnect to the server after reseting the existing socket
            //console.log("Reconnecting");
            this.reset();
            //console.log("keys in subscription " + this.subscriptions.size)
            this.reconnecting = true;
            //console.log("Calling Connect()");
            this.connect('', '');
        }
    }

    private checkForOpen() {
        if (this.socket.readyState == ReadyStateOpen) {
            // We got to open state, so now do the onConnect callback
            if (typeof this.onConnected == 'function') {
                // Connected, notify callback if registered
                this.onConnected(this);
            }

            // Stop the checker
            clearInterval(this.stateChecker);
        }
        else if (this.socket.readyState == ReadyStateConnecting) {
            // We're still in connecting state, try again.
            // Interval will still call this, so do nothing.
        }
        else {
            // We're in closing or closed, very bad.  Send back an error
            // TODO: determine what to do with an error.
            this.onError(new Event("Socket closed while opening"));

            this.reset();
        }
    }

    ////////////////////////
    // WEBSOCKET CALLBACKS
    ////////////////////////

    onOpen(event: Event) {
        //console.log("keys in subscription " + this.subscriptions.size)
        if (this.socket.readyState == ReadyStateConnecting) {
            // The socket is actually in CONNECTING and not yet in OPEN.  Setup an interval to look for this and call the callback only after it happens.
            // Add 100ms check callback.
            this.stateChecker = setInterval(this.checkForOpen.bind(this), 100);
        }
        else if (this.socket.readyState == ReadyStateOpen) // OPEN
        {
            this.connected = true;

            // TEST
            //this.socket.send(this.SGFormat(SGMessageTypeSend, "testchan", "abcd"));
            // ENDTEST

            // Use the following code when authentication tokens are supported.
            // if (this.authToken)
            //{
            //    this.socket.send(this.SGFormat(SGMessageTypeAuth, "", this.authToken));
            //}

            if (this.reconnecting) {
                //console.log("reconnected...");
                // As we were reconnecting resubscribe to those messages that need it.
                //                for (const subscription in this.subscriptions)
                //                {
                //                    //console.log("subscription " + subscription);
                //                    let subinfo = this.subscriptions[subscription];
                //                    if (subinfo.reconnect)
                //                    {
                //                        //console.log("resubscribing");
                //                        // Send subscription message.
                //                        let msg = this.SGFormat(SGMessageTypeSubscribe, subinfo.channel);
                //                        if (typeof msg != 'undefined')
                //                        {
                //                            //console.log("sending");
                //                            this.socket.send(msg);
                //                        }
                //                    }
                //                    else
                //                    {
                //                        //console.log("deleting");
                //                        // Remove the subscription.
                //                        delete this.subscriptions[subscription];
                //                    }
                //                }
                // As we were reconnecting resubscribe to those messages that need it.
                //console.log("keys in subscription " + this.subscriptions.size)
                this.subscriptions.forEach((subscription, channel) => {
                    //console.log("subscription " + subscription);
                    if (subscription.reconnect) {
                        //console.log("Resubscribing to " + channel + "/" + subscription.channel);
                        // Send subscription message.
                        const msg = this.SGFormat(SGMessageTypeSubscribe, subscription.channel);
                        if (typeof msg != 'undefined') {
                            this.databytesOutSinceLastConnection += msg.length;
                            this.socket.send(msg);
                        }
                    }
                    else {
                        //console.log("deleting");
                        // Remove the subscription.
                        this.subscriptions.delete(subscription.channel);
                    }
                });
                //console.log("Completed resub");

            }

            this.reconnecting = false;

            //clientDebugOutput(this, String(this.socket.readyState));

            if (typeof this.onConnected == 'function') {
                // Connected, notify callback if registered
                this.onConnected(this);
            }
        }
        //else {
        //
        //}

    }

    onClose(event: CloseEvent) {
        clientDebugOutput(this, "Got close with reason" + event.reason);
        this.connected = false
        this.reconnecting = false

        if (this.disconnecting == true) {
            // Only reset if disconnect() was called
            clientDebugOutput(this, "Resetting due to onClose and disconnecting");
            this.reset();

            // Clear out the subscriptions now
            this.subscriptions.forEach((subscription, channel) => {
                subscription.channel = undefined;
                subscription.reconnect = undefined;
                subscription.cb = undefined;
                subscription.token = undefined;
                this.subscriptions.delete(channel);
            });

            //this.subscriptions.key
        }
        else {
            if ( !this.connected )
            {
                // The connection was closed before starting.  Fail this without a retry
                clientDebugOutput( this, "Resetting due to onClose prior to an onOpen" );
                this.reset();
                this.subscriptions.forEach( ( subscription, channel ) =>
                {
                    subscription.channel = void 0;
                    subscription.reconnect = void 0;
                    subscription.cb = void 0;
                    subscription.token = void 0;
                    this.subscriptions.delete( channel );
                } );
                // Generate an error event
                let errorEvent = new Event( "error" );
                errorEvent.error = new Object();
                errorEvent.error.message = "Can't connect";
                errorEvent.error.code = 1006;
                errorEvent.error.type = "websocket";
                console.log( "Sending event " + errorEvent );
                this.onError( errorEvent );
            }
            clientDebugOutput(this, "hit onClose without disconnecting true");
        }

        // Disconnected, notify callback if registered
        if (typeof this.onDisconnected == 'function') {
            //console.log( "calling onDisconnected callback" );
            clientDebugOutput(this, "calling onDisconnected callback");
            this.onDisconnected(this);
        }

        // Reset bandwidth information since this connection is gone
        this.dbSecondsSinceLastUpdate = 0;
        this.dbMinutesSinceLastUpdate = 0;
        this.databytesAtLastSecUpdate = 0;
        this.databytesAtLastMinUpdate = 0;
        this.databytesAtLastHourUpdate = 0;
        this.databytesSinceLastConnection = 0;
        this.databytesOutAtLastSecUpdate = 0;
        this.databytesOutAtLastMinUpdate = 0;
        this.databytesOutAtLastHourUpdate = 0;
        this.databytesOutSinceLastConnection = 0;

        // Reconnection is handled in the keep-alive
    }

    processedbuffer(buffer: Uint8Array<ArrayBufferLike>, field: number) {
        //console.log(field + " buffer length " + buffer.byteLength);
        // TODO: fix this typing
        let newint8buf = new Uint8Array<ArrayBufferLike>(buffer as unknown as ArrayBufferLike);

        // Save off the bytes that were received
        this.databytesSinceLastConnection += newint8buf.length;

        if (field != this.nextMsgCount) {
            //console.log("out of order buffer " + field);
            this.bufferList.set(field, newint8buf);
            return;
        }

        // increment the message counter to know to work on the next one.
        this.nextMsgCount++;

        if (this.prevint8buf != null) {
            // Combine with the previous buffer.
            const tmpint8buf = [...this.prevint8buf, ...newint8buf];
            newint8buf = tmpint8buf as unknown as Uint8Array<ArrayBufferLike>; // TODO: review the need to cast to unknown here.
        }

        // Update current data
        this.curDataLength += buffer.byteLength;

        if (this.msgcode == null) {
            // New message
            this.msgcode = String.fromCharCode(newint8buf[0]);
        }

        if (this.chnlen == -1) {
            if (this.curDataLength >= 4) {
                // Got enough data to get channel length
                this.chnlen = parseInt(String.fromCharCode(newint8buf[1]) + String.fromCharCode(newint8buf[2]) + String.fromCharCode(newint8buf[3]), 10);
            }
            else {
                // Out of data, so return and process again with new data.
                this.prevint8buf = newint8buf;

                const tmpbuf = this.bufferList.get(this.nextMsgCount);
                if (tmpbuf != undefined) {
                    //console.log("previous buffer " + this.nextMsgCount);
                    this.bufferList.delete(this.nextMsgCount);
                    this.processedbuffer(tmpbuf, this.nextMsgCount);
                }
                return;
            }
        }

        if (this.msglen == -1) {
            if (this.curDataLength >= 8) {
                // Got enough data to get message length
                if ((newint8buf[4] & 0x80) == 0x80) {
                    //console.log("Large msglen mode");
                    this.msglen = ((newint8buf[4] & 0x7F) << 24) + (newint8buf[5] << 16) + (newint8buf[6] << 8) + (newint8buf[7] << 0);
                }
                else {
                    this.msglen = parseInt(String.fromCharCode(newint8buf[4]) + String.fromCharCode(newint8buf[5]) + String.fromCharCode(newint8buf[6]) + String.fromCharCode(newint8buf[7]));
                }
            }
            else {
                // Out of data, so return and process again with new data.
                this.prevint8buf = newint8buf;

                const tmpbuf = this.bufferList.get(this.nextMsgCount);
                if (tmpbuf != undefined) {
                    //console.log("previous buffer " + this.nextMsgCount);
                    this.bufferList.delete(this.nextMsgCount);
                    this.processedbuffer(tmpbuf, this.nextMsgCount);
                }
                return;
            }
        }

        if (this.channel == null) {
            if (this.curDataLength >= (8 + this.chnlen)) {
                // Got enough data to get channel length
                this.channel = this.td.decode(newint8buf.subarray(8, 8 + this.chnlen));
                //let msg = td.decode(newint8buf.subarray(8 + this.chnlen, buffer.byteLength - 8 - this.chnlen));
                //console.log("start of msg " + msg.slice(0, 50));

            }
            else {
                // Out of data, so return and process again with new data.
                this.prevint8buf = newint8buf;

                const tmpbuf = this.bufferList.get(this.nextMsgCount);
                if (tmpbuf != undefined) {
                    //console.log("previous buffer " + this.nextMsgCount);
                    this.bufferList.delete(this.nextMsgCount);
                    this.processedbuffer(tmpbuf, this.nextMsgCount);
                }
                return;
            }
        }

        const totalmsglen = 8 + this.chnlen + this.msglen;
        if (this.curDataLength == totalmsglen) {
            //console.log("msg " + this.msgcode + "  chnlen " + this.chnlen + "  msglen " + this.msglen);
            // Got all the data.  Split based on message code.  Must only be Sent or SentRaw
            switch (this.msgcode) {
                case SGMessageTypeSent:
                    if (this.subscriptions.has(this.channel)) {
                        const msg = this.td.decode(newint8buf.subarray(8 + this.chnlen, this.curDataLength));
                        this.subscriptions.get(this.channel).cb(this, this.channel, msg);
                    }
                    break;
                case SGMessageTypeSentRaw:
                    if (this.subscriptions.has(this.channel)) {
                        this.subscriptions.get(this.channel).cbraw(this, this.channel, newint8buf.subarray(8 + this.chnlen, this.curDataLength));
                    }
                    break;
            }

            // Reset for next message
            this.msgcode = null;
            this.chnlen = -1
            this.msglen = -1;
            this.channel = null;
            this.prevint8buf = null;
            this.curDataLength = 0;
            this.prevint8buf = null;
        }
        else if (this.curDataLength > totalmsglen) {
            // Got too much data!
            if (this.subscriptions.has(this.channel)) {
                onBinaryMessage(this, this.channel, newint8buf.subarray(8 + this.chnlen, this.curDataLength));
                //this.subscriptions[channel].cb(this, channel, msg);
            }


            // Reset for next message
            // TODO: send the remainder back around for processing?
            this.msgcode = null;
            this.chnlen = -1
            this.msglen = -1;
            this.channel = null;
            this.prevint8buf = null;
            this.curDataLength = 0;
            this.prevint8buf = null;
        }
        else {
            // Out of data, so return and process again with new data.
            this.prevint8buf = newint8buf;
        }

        const tmpbuf = this.bufferList.get(this.nextMsgCount);
        if (tmpbuf != undefined) {
            //console.log("previous buffer " + this.nextMsgCount);
            this.bufferList.delete(this.nextMsgCount);
            this.processedbuffer(tmpbuf, this.nextMsgCount);
        }
    }

    rejectedBuffer(field: number) {
        let tmpbuf = this.bufferList.get(field);
        if (tmpbuf == undefined) {
            tmpbuf = new Uint8Array(); // was ArrayBuffer()
            this.bufferList.set(field, tmpbuf);
        }
    }

    // We're using this method to return back to the socket as fast as possible to prevent any message timeouts due to processing.
    // The message will be processed immediately, but on the onMessageTick() call.
    async onMessage(event: MessageEvent) {
        this.InputQueue.push(event);
        if (this.InputQueueInterval == null)
            this.InputQueueInterval = setTimeout(this.onMessageTick.bind(this), 0);
    }

    async onMessageTick() {
        if (this.InputQueue.length == 0) {
            this.InputQueueInterval = null;
            return;
        }

        let limit = 20;    // only handle 20 messages max per event.  This can happen when messages get backed up.

        do
        {
            const event = this.InputQueue.shift();
            // Get the basic parts of the message
            //clientDebugOutput(this, "onMessage got message with event type " + event.type + " msgdata of type " + (typeof event.data));

            //Profile.log("onMessage received", "onMessage");

            //console.log("onMessage: Got event type " + event.type);
            if (event.type == 'message') {
                const msgdata = event.data;
                if (typeof msgdata == 'object') {
                    //console.log("msgdata type " + typeof(msgdata) + "  size " + msgdata.size )

                    //var testdatacmd = msgdata.slice(0, 1);
                    //var testdatachnlen = msgdata.slice(1, 4);
                    //var testdatamsglen = msgdata.slice(4, 8);

                    //testdatacmd.arrayBuffer().then((buffer) => { this.processedbuffer(buffer, "cmd"); });
                    //testdatachnlen.arrayBuffer().then((buffer) => { this.processedbuffer(buffer, "chn"); });
                    //testdatamsglen.arrayBuffer().then((buffer) => { this.processedbuffer(buffer, "msg"); });
                    const tmpnumber = this.binaryMsgCount++;
                    //console.log("<<<<<<<< " + tmpnumber + " buffer length " + msgdata.size + " >>>>>>>>");
                    //console.log("this.socket = " + this.socket.binaryType);
                    //console.log("msgdata type: " + typeof msgdata);
                    //console.log("msgdata type: " + msgdata.constructor.name);
                    //console.log("this.socket constructor" + this.socket.constructor.name);
                    //console.log("msgdata.arrayBuffer type: " + typeof msgdata.arrayBuffer);

                    //Profile.log(`Binary message received ${tmpnumber}`, "binaryMessage");
                    if (msgdata.arrayBuffer) {
                        msgdata.arrayBuffer().then((buffer: ArrayBuffer) => { this.processedbuffer.call(this, buffer, tmpnumber); }, (error) => { this.rejectedBuffer.call(this, tmpnumber); });
                    } else {
                        convertBlobToArrayBuffer(msgdata).then((buffer) => { this.processedbuffer.call(this, buffer, tmpnumber); }, (error) => { this.rejectedBuffer.call(this, tmpnumber); });
                    }

                    //console.log("cmd " + result + "   chnlen " + testdatachnlen + "  msglen " + testdatamsglen);
                    setTimeout(this.onMessageTick.bind(this), 0);
                    return;
                }

                // TODO: Add code to detect split websocket messages.

                // Save off the bytes that were received
                this.databytesSinceLastConnection += msgdata.length;

                const type = msgdata.slice(0, 1);

                const csize = Number(msgdata.slice(1, 4));
                //console.log("csize " + csize);
                //console.log("channel " + msgdata.slice(8, 8 + csize));
                //console.log("raw msize " + msgdata.slice(4,8));

                const msize = Number(msgdata.slice(4, 8));

                // Convert to blob to slice out the data we expect, if necessary
                const sample = new Blob([msgdata]);
                let channel = "";
                let msg = "";
                if (sample.size == msgdata.length) {
                    // String is ASCII or UTF8 with single byte chars only
                    //console.log("msgdata type " + typeof (msgdata) + "  length " + msgdata.length)

                    //if ((msize + csize + 8) != msgdata.length)
                    //{
                    //    console.log("Msg length " + msgdata.length + " is not equal to message header reqs: csize " + csize + ", msize " + msize);
                    //}

                    if ((8 + csize) <= msgdata.length) {
                        channel = msgdata.slice(8, 8 + csize);
                    }

                    if ((8 + csize + msize) <= msgdata.length) {
                        msg = msgdata.slice(8 + csize, 8 + csize + msize);
                    }
                    //console.log("msg <" + msg + ">");
                }
                else {
                    //console.log("UTF8 msgdata <" + msgdata + ">");
                    // String is UTF8 encoding with some 2+ byte characters.  Extract the byte portions then convert back to UTF8
                    //if ((msize + csize + 8) != sample.size)
                    //{
                    //    console.log("Sample size " + sample.size + " is not equal to message header reqs: csize " + csize + ", msize " + msize);
                    //}

                    if (((8 + csize) <= sample.size) && ((8 + csize + msize) <= sample.size)) {
                        const intermediateChannel = sample.slice(8, 8 + csize);
                        const intermediateMsg = sample.slice(8 + csize, 8 + csize + msize);
                        const channelAsync = intermediateChannel.text();
                        const msgAsync = intermediateMsg.text();
                        const promises = [];
                        promises.push(channelAsync);
                        promises.push(msgAsync);
                        await Promise.all(promises).then(values => { /*console.log("values: ", values);*/ channel = values[0]; msg = values[1]; });
                        //console.log("Done with await, channel " + channel + ", msg <" + msg + ">");
                    }
                }

                //clientDebugOutput(this, "onMessage got message type " + type);

                // MARK profile each callback here after tracing.

                // Parse the message for what to do.
                switch (type) {
                    case SGMessageTypeSubscribeResponse:
                        //console.log("Adding subscription");
                        if (this.subscriptions.has(channel)) {
                            this.subscriptions.get(channel).token = msg;
                            clientOnSubscribed(this, channel);
                        }
                        //console.log("subscriptions[" + channel + "]  " + this.subscriptions.get(channel).token)
                        break;
                    case SGMessageTypeUnsubscribeResponse:
                        if (this.subscriptions.has(channel)) {
                            this.subscriptions.get(channel).cb = null; // was callback
                            this.subscriptions.get(channel).cbraw = null; // was callbackraw
                            this.subscriptions.get(channel).token = null;
                            this.subscriptions.get(channel).channel = null;
                            this.subscriptions.delete(channel);
                            clientOnUnsubscribed(this, channel);
                        }
                        break;
                    case SGMessageTypeSent:
                        if (this.subscriptions.has(channel)) {
                            this.subscriptions.get(channel).cb(this, channel, msg);
                        }
                        break;
                    case SGMessageTypeSentRaw:
                        if (this.subscriptions.has(channel)) {
                            this.subscriptions.get(channel).cbraw(this, channel, msg);
                        }
                        break;
                    case SGMessageTypeRequestTimeResponse:
                        //Profile.log("SGMessageTypeRequestTimeResponse received", "SGMessageTypeRequestTimeResponse");
                        clientOnRequestTimeResponse(this, channel, msg);
                        break;
                    case SGMessageTypeIncrementCredit:
                        clientOnIncrementCredit(this, channel, msg);
                        break;
                    case SGMessageTypeRequestTimeErrorResponse:
                        //Profile.log("SGMessageTypeRequestTimeErrorResponse received", "SGMessageTypeRequestTimeErrorResponse");
                        //clientDebugOutput(this, "Got SGMessageTypeRequestTimeErrorResponse channel <" + channel + ">  msg <" + msg + ">");
                        clientOnRequestTimeErrorResponse(this, channel, msg);
                        break;
                    case SGMessageTypeKeepAlive:
                        //console.log("got keepalive");
                        break;
                    case SGMessageTypeRecalibrate:
                        console.log( "got recalibrate" );
                        if ( typeof this.onControl == 'function' )
                        {
                            clientDebugOutput( this, "calling onControl callback" );
                            // TODO: the string sent back should be a class with an enum for recalibrate to allow additional control messages in the future.
                            this.onControl( this, channel, "recalibrate" );
                        }
                        break;
                    default:
                        // Other messages that aren't supported currently
                        break;
                }
            }
        } while ( ( --limit > 0 ) && ( this.InputQueue.length > 0 ) );

        setTimeout(this.onMessageTick.bind(this), 0);
    }

    onError(event: Event) {
        clientDebugOutput(this, "Received websocket error " + event);
        if (this.reconnecting) {
            this.reconnecting = false;
        }
        clientOnError(this, event);
    }

    ////////////////////////
    // EXTERNAL METHODS
    ////////////////////////

    createClient(clientId: number, sgmc: SGMessagingClient) {
        this.connected = false;
        this.reconnecting = false;
        this.initialized = false;
        this.clientId = clientId;
        this.sgmc = sgmc;
    }

    // Set the messaging server domanin name and port.
    // NOTE: This is not a true URL and should not have the
    //       'https://' prefix.  An address without a port
    //       number will use the websockets default 8084.
    setClusterUrl(url: string) {
        if (typeof url == 'string') {
            this.msgServer = url;
            const tmpStr = this.msgServer.split(":");
            if (tmpStr.length == 1) {
                this.msgServer = this.msgServer + ":8084";
            }
        }
    }

    // Set the useSsl flag.
    setUseSsl(useSsl: boolean) {
        if (typeof useSsl == 'boolean') {
            this.useSsl = useSsl;
        }
    }

    // Request connection to the messaging server.
    // NOTE: appkey and token are not used currently.
    connect(appkey?: string | number, token?: string | number) {
        // Check if a connection has already been made.
        if (this.connected == true) {
            return;
        }

        //console.log("Starting connect()");
        if (typeof this.msgServer != 'string') {
            // TODO: return an error.
            return;
        }

        if (window.MozWebSocket) {
            //console.log("Shifted to MozWebSocket");
            window.WebSocket = window.MozWebSocket;
        }

        try
        {
            //console.log( "Attempting connection to " + this.msgServer );
            if ( this.useSsl )
            {
                this.socket = new WebSocket( "wss://".concat( this.msgServer ), [ SGMessagingWebSocketSubProtocol, "test" ] );
            }
            else
            {
                this.socket = new WebSocket( "ws://".concat( this.msgServer ), [ SGMessagingWebSocketSubProtocol, "test" ] );
            }

            //console.log("Binding socket callbacks");
            this.socket.onopen = this.onOpen.bind( this );
            this.socket.onmessage = this.onMessage.bind( this );
            this.socket.onclose = this.onClose.bind( this );
            this.socket.onerror = this.onError.bind( this );

            //clientDebugOutput(this, "connecting");

            if ( !this.initialized )
            {
                // Add 1 second keep-alive callback.
                // TODO: Fix all keepalive logic - should not go out when data is moving around.
                this.keepaliveTimer = setInterval( this.keepalive.bind( this ), 100000 );

                this.initialized = true;
            }
        } catch ( error )
        {
            return;
        }
    }

    disconnect() {
        clientDebugOutput(this, "entering disconnect");
        this.connected = false;
        this.disconnecting = true;

        //console.log("checking keepalive timer");
        if (this.keepaliveTimer) {
            //console.log("clearing keepalive timer");
            clearInterval(this.keepaliveTimer);
            this.keepaliveTimer = null;
        }

        //console.log("checking state checker");
        if (this.stateChecker) {
            //console.log("clearing state checker");
            clearInterval(this.stateChecker);
            this.stateChecker = null;
        }

        // This may be replicated within the reset() call, which is fine.
        this.initialized = false;

        clientDebugOutput(this, "checking socket");
        if (this.socket) {
            if (this.socket.readyState == ReadyStateOpen) {
                clientDebugOutput(this, "closing socket");
                this.socket.close();
            }
            else if (this.socket.readyState == ReadyStateConnecting) {
                clientDebugOutput(this, "socket connecting, resetting");
                this.reset();
            }
            else if (this.socket.readyState == ReadyStateClosed) {
                // Socket already closed, so reset it here.
                clientDebugOutput(this, "socket already closed, resetting");
                this.reset();
            }
            else {
                clientDebugOutput(this, "socket in bad state");
            }
            // else the socket is currently closing so process it in the callback.
        }
        else {
            clientDebugOutput(this, "no socket");
            // No socket present, so process close here.
            this.reset();

            // Clear out the subscriptions now
            this.subscriptions.forEach((subscription, channel) => {
                subscription.channel = undefined;
                subscription.reconnect = undefined;
                subscription.cb = undefined;
                subscription.token = undefined;
                this.subscriptions.delete(channel);
            });

            //this.subscriptions.key

            // Disconnected, notify callback if registered
            if (typeof this.onDisconnected == 'function') {
                //console.log("calling onDisconnected callback");
                this.onDisconnected(this);
            }
        }
    }

    reset() {
        //console.log("entering reset");
        this.connected = false;
        this.disconnecting = false;

        //console.log("clearing keepalive timer");
        if (this.keepaliveTimer) {
            clearInterval(this.keepaliveTimer);
            this.keepaliveTimer = null;
        }

        //console.log("clearing state checker");
        if (this.stateChecker) {
            clearInterval(this.stateChecker);
            this.stateChecker = null;
        }

        this.initialized = false;

        //console.log("checking socket");
        if (this.socket) {
            this.socket.onopen = null;
            this.socket.onmessage = null;
            this.socket.onerror = null;
            this.socket.onclose = null;

            if (this.socket.readyState == ReadyStateConnecting || this.socket.readyState == ReadyStateOpen) {
                this.socket.close();
            }

            this.socket = null;
        }

        //console.log("reset complete");
    }

    // Subscribe for messages from a particular channel.  The callback returns the instance of the client,
    // the channel string, and the message.
    // NOTE: Only one callback per subscription is allowed.
    // NOTE: The message is not deserialized.
    // TODO: fix the callback types
    subscribe(channel: string, resubOnReconnect: boolean, callback: SGSubscriptionCallback, callbackraw: SGSubscriptionCallbackRaw, metadataChannel: string, replicateChannel: string, updateChannels: string[], audioChannelFlag: boolean, videoChannelFlag: boolean, ownedFlag: boolean, privateFlag: boolean, channelSendOnlyFlag: boolean): boolean
    {
        // Check that input parameters are valid.
        if (typeof channel != 'string') {
            // channel must be a string.
            //console.log("subscribe 'channel' parameter must be a string");
            return false;
        }

        if (typeof resubOnReconnect != 'boolean') {
            // resubOnReconnect must be a string.
            //console.log("subscribe 'resubOnReconnect' parameter must be a boolean");
            return false;
        }

        if (typeof callback != 'function') {
            // callback must be a function.
            //console.log("subscribe 'callback' parameter must be a function");
            return false;
        }

        // Check if this channel was already subscribed to.
        if (this.subscriptions.has(channel) && (this.subscriptions.get(channel).token != "")) {
            // Current subscription, so replace the existing reconnect and callback settings.
            this.subscriptions.get(channel).reconnect = resubOnReconnect;
            this.subscriptions.get(channel).cb = callback;
            this.subscriptions.get(channel).cbraw = callbackraw;
        }
        else {
            // This is a new subscription, so add it.
            const newSubscription: SGMessagingClientFSubscription = {
                channel: channel,
                reconnect: resubOnReconnect,
                cb: callback,
                cbraw: callbackraw,
                token: ""
            };
            this.subscriptions.set(channel, newSubscription);

            //console.log("subscribe keys in subscription " + this.subscriptions.size)

            // Setup and serialize the subscribe structure if params are present
            const subParamsObj = {};
            subParamsObj["MetadataChannelName"] = metadataChannel;
            subParamsObj["ReplicateChannelName"] = replicateChannel;
            subParamsObj["UpdateChannels"] = updateChannels;
            subParamsObj["AudioChannelFlag"] = audioChannelFlag;
            subParamsObj["VideoChannelFlag"] = videoChannelFlag;
            subParamsObj["OwnedFlag"] = ownedFlag;
            subParamsObj["PrivateFlag"] = privateFlag;
            subParamsObj["ChannelSentOnlyFlag"] = channelSendOnlyFlag;

            const subParams: string = JSON.stringify(subParamsObj);

            // Subscribe the channel to the messaging server.
            const msg = this.SGFormat(SGMessageTypeSubscribe, channel, subParams);
            if (typeof msg != 'undefined') {
                try {
                    //console.log("state before send is " + this.socket.readyState);
                    if (this.socket.readyState != ReadyStateOpen) {
                        // Connection hasn't been completed, or it has been closed/is closing.
                        return false;
                    }
                    this.databytesOutSinceLastConnection += msg.length;
                    this.socket.send(msg);
                }
                catch (error) {
                    if (error instanceof DOMException) {
                        if (error.name != "InvalidStateError") {
                            //alert("received uncaught DOMException");
                            //throw error;
                        }
                        else {
                            //console.log("Got InvalidStateError for subscribe")
                        }
                    }

                    return false;
                }
            }
            else {
                return false;
            }
        }

        return true;
    }

    // Unsubscribe for messages from a particilar channel.  The token received from the initial
    // subscbription is sent back.
    unsubscribe(channel: string): boolean {
        // Check that input parameters are valid.
        if (typeof channel != 'string') {
            // channel must be a string.
            //console.log("unsubscribe 'channel' parameter must be a string");
            return false;
        }

        if (this.subscriptions.has(channel)) {
            // Unsubscribe the channel from the messaging server.
            const msg = this.SGFormat(SGMessageTypeUnsubscribe, channel, this.subscriptions.get(channel).token);
            if (typeof msg != 'undefined') {
                try {
                    //console.log("state before send is " + this.socket.readyState);
                    if (this.socket.readyState != ReadyStateOpen) {
                        // Connection hasn't been completed, or it has been closed/is closing.
                        return false;
                    }
                    this.databytesOutSinceLastConnection += msg.length;
                    this.socket.send(msg);
                }
                catch (error) {
                    if (error instanceof DOMException) {
                        if (error.name != "InvalidStateError") {
                            //alert("received uncaught DOMException");
                            //throw error;
                        }
                        else {
                            //console.log("Got InvalidStateError for unsubscribe")
                        }
                    }

                    return false;
                }
            }
        }

        return true;
    }

    sendAuthenticate(channel: string, token: string): boolean {
        // Check that input parameters are valid.
        if ((typeof channel != 'string') || (typeof token != 'string')) {
            // channel must be a string.
            //console.log("sendAuthenticate 'channel' and 'token' parameters must be strings");
            return false;
        }

        const msg = this.SGFormat(SGMessageTypeAuth, channel, token);
        if (typeof msg != 'undefined') {
            try {
                //console.log("state before send is " + this.socket.readyState);
                if (this.socket.readyState != ReadyStateOpen) {
                    // Connection hasn't been completed, or it has been closed/is closing.
                    return false;
                }
                this.databytesOutSinceLastConnection += msg.length;
                this.socket.send(msg);
            }
            catch (error) {
                if (error instanceof DOMException) {
                    if (error.name != "InvalidStateError") {
                        //alert("received uncaught DOMException");
                        //throw error;
                    }
                    else {
                        //console.log("Got InvalidStateError for sendAuthenticate")
                    }
                }
            }

            return false;
        }

        return true;
    }

    sendIncrementCredit(credits: number)
    {
        if (typeof credits != "number")
        {
            return false;
        }
        const msg = this.SGFormat(SGMessageTypeIncrementCredit, "", credits.toString());
        if (typeof msg != "undefined")
        {
            try
            {
                if (this.socket.readyState != ReadyStateOpen)
                {
                    return false;
                }
                this.databytesOutSinceLastConnection += msg.length;
                this.socket.send(msg);
            } catch (error)
            {
                if (error instanceof DOMException)
                {
                    if (error.name != "InvalidStateError")
                    {
                        //alert("received uncaught DOMException");
                        //throw error;
                    }
                    else
                    {
                        //console.log("Got InvalidStateError for sendAuthenticate")
                    }
                }
            }
            return false;
        }
        return true;
    }

    sendChannelCommandPause(channel)
    {
        if (typeof channel != "string")
        {
            return false;
        }
        if (!this.subscriptions.has(channel) || this.subscriptions.get(channel).token == "")
        {
            return false;
        }
        const utf8Encoder = new TextEncoder();
        const encodedToken = utf8Encoder.encode(this.subscriptions.get(channel).token);
        const binmsg = new Uint8Array(encodedToken.length + 3);
        binmsg[0] = 1;
        binmsg[1] = 0;
        binmsg[2] = encodedToken.length;
        binmsg.set(encodedToken, 3);
        const msg = this.SGFormat(SGMessageTypeChannelCommandRequest, channel, binmsg);
        if (typeof msg != "undefined")
        {
            try
            {
                if (this.socket.readyState != ReadyStateOpen)
                {
                    return false;
                }
                this.databytesOutSinceLastConnection += msg.length;
                this.socket.send(msg);
            } catch (error)
            {
                if (error instanceof DOMException)
                {
                    if (error.name != "InvalidStateError")
                    {
                        //alert("received uncaught DOMException");
                        //throw error;
                    }
                    else
                    {
                        //console.log("Got InvalidStateError for sendAuthenticate")
                    }
                }
            }
            return false;
        }
        return true;
    }

    sendChannelCommandResume(channel: string)
    {
        if (typeof channel != "string")
        {
            return false;
        }
        if (!this.subscriptions.has(channel) || this.subscriptions.get(channel).token == "")
        {
            return false;
        }
        const utf8Encoder = new TextEncoder();
        const encodedToken = utf8Encoder.encode(this.subscriptions.get(channel).token);
        const binmsg = new Uint8Array(encodedToken.length + 3);
        binmsg[0] = 2;
        binmsg[1] = 0;
        binmsg[2] = encodedToken.length;
        binmsg.set(encodedToken, 3);
        const msg = this.SGFormat(SGMessageTypeChannelCommandRequest, channel, binmsg);
        if (typeof msg != "undefined")
        {
            try
            {
                if (this.socket.readyState != ReadyStateOpen)
                {
                    return false;
                }
                this.databytesOutSinceLastConnection += msg.length;
                this.socket.send(msg);
            } catch (error)
            {
                if (error instanceof DOMException)
                {
                    if (error.name != "InvalidStateError")
                    {
                        //alert("received uncaught DOMException");
                        //throw error;
                    }
                    else
                    {
                        //console.log("Got InvalidStateError for sendAuthenticate")
                    }
                }
            }
            return false;
        }
        return true;
    }

    send(channel: string, msg: string): boolean {
        // Check that input parameters are valid.
        if (typeof channel != 'string') {
            // channel must be a string
            return false;
        }

        if (typeof msg != 'string') {
            // debugMsg must be a string
            return false;
        }

        if (this.connected) {
            try
            {
                msg = this.SGFormat(SGMessageTypeSend, channel, msg) as string;
                if (typeof msg != 'undefined')
                {
                    this.databytesOutSinceLastConnection += msg.length;
                    this.socket.send(msg);
                }
            }
            catch (error)
            {
                if (error instanceof DOMException)
                {
                    if (error.name != "InvalidStateError")
                    {
                        //alert("received uncaught DOMException");
                        //throw error;
                    }
                    else
                    {
                        //console.log("Got InvalidStateError");
                    }
                }
                else
                {
                    //console.log("Got error " + error);
                }

                return false;
            }

            return true;
        }
    }

    sendRaw(channel: string, msg: Uint8Array): boolean {
        // Check that input parameters are valid.
        if (typeof channel != 'string') {
            // channel must be a string
            return false;
        }

        if (!(msg instanceof Uint8Array)) {
            // debugMsg must be a Uint8Array
            return false;
        }

        if (this.connected) {
            try {
                msg = this.SGFormat(SGMessageTypeSendRaw, channel, msg) as Uint8Array;
                if (typeof msg != 'undefined') {
                    this.databytesOutSinceLastConnection += msg.length;
                    this.socket.send(msg);
                }
            }
            catch (error) {
                if (error instanceof DOMException)
                {
                    if (error.name != "InvalidStateError")
                    {
                        //alert("received uncaught DOMException");
                        //throw error;
                    }
                    else
                    {
                        //console.log("Got InvalidStateError");
                    }
                }
                else
                {
                    //console.log("Got error " + error);
                }

                return false;
            }

            return true;
        }
    }

    sendDebug(channel: string, debugMsg: string): boolean {
        //console.log("sendDebug: [" + channel + "] " + debugMsg);
        // Check that input parameters are valid.
        if (typeof channel != 'string') {
            // channel must be a string
            return false;
        }

        if (typeof debugMsg != 'string') {
            // debugMsg must be a string
            return false;
        }

        if (this.connected) {
            try {
                const tmsg = "RealTimeMessage" + SerializingDelimiter + "0" + SerializingDelimiter + "0" + SerializingDelimiter + MessageType_Text + SerializingDelimiter + DebugChannelPrefix + channel + SerializingDelimiter + debugMsg;
                const msg = this.SGFormat(SGMessageTypeSend, DebugChannelPrefix + channel, tmsg);
                if (typeof msg != 'undefined') {
                    this.databytesOutSinceLastConnection += msg.length;
                    this.socket.send(msg);
                }
            }
            catch (error) {
                if (error instanceof DOMException)
                {
                    if (error.name != "InvalidStateError")
                    {
                        //alert("received uncaught DOMException");
                        //throw error;
                    }
                    else
                    {
                        //console.log("Got InvalidStateError");
                    }
                }
                else
                {
                    //console.log("Got error " + error);
                }

                return false;
            }

            return true;
        }
    }

    requestTime(channel: string): boolean {
        // Check that input parameters are valid.
        if (typeof channel != 'string') {
            // channel must be a string
            return false;
        }

        if (this.connected) {
            //clientDebugOutput(this, "requestTime connected");
            try {
                const msg = this.SGFormat(SGMessageTypeRequestTime, channel);
                //clientDebugOutput(this, "requestTime formatted request");
                if (typeof msg != 'undefined') {
                    this.databytesOutSinceLastConnection += msg.length;
                    this.socket.send(msg);
                    //clientDebugOutput(this, "requestTime sent request");
                }
            }
            catch (error) {
                if (error instanceof DOMException) {
                    if (error.name != "InvalidStateError") {
                        //alert("received uncaught DOMException");
                        //throw error;
                    }
                    else {
                        //console.log("Got InvalidStateError");
                    }
                }
                else {
                    //console.log("Got error " + error);
                }

                //clientDebugOutput(this, "requestTime got error");

                return false;
            }

            return true;
        }
        else {
            //clientDebugOutput(this, "requestTime not connected");

            return false;
        }
    }

    requestTimeError(channel: string, ticks: number): boolean {
        // Check that input parameters are valid.
        if ((typeof channel != 'string') || (typeof ticks != 'number')) {
            // channel must be a string and ticks must be a big integer (long)
            clientDebugOutput(this, "Invalid types to requestTimeError");
            return false;
        }

        if (this.connected) {
            //clientDebugOutput(this, "requestTimeError connected");
            try {
                const msg = this.SGFormat(SGMessageTypeRequestTimeError, channel, ticks.toString());
                //clientDebugOutput(this, "requestTimeError formatted request");
                if (typeof msg != 'undefined') {
                    this.databytesOutSinceLastConnection += msg.length;
                    this.socket.send(msg);
                    //clientDebugOutput(this, "requestTimeError sent request");
                }
            }
            catch (error) {
                if (error instanceof DOMException) {
                    if (error.name != "InvalidStateError") {
                        //alert("received uncaught DOMException");
                        //throw error;
                    }
                    else {
                        //console.log("Got InvalidStateError");
                    }
                }
                else {
                    //console.log("Got error " + error);
                }

                clientDebugOutput(this, "requestTimeError got error");

                return false;
            }

            return true;
        }
        else {
            clientDebugOutput(this, "requestTimeError not connected");

            return false;
        }
    }

    setSendQueueLimit(limit: number): boolean {
        // Check that input parameters are valid.
        if (typeof limit != 'number') {
            // limit must be an integer
            clientDebugOutput(this, "Invalid type to setSendQueueLimit");
            return false;
        }

        if (this.connected) {
            //clientDebugOutput(this, "setSendQueueLimit connected");
            try {
                const msg = this.SGFormat(SGMessageTypeSetSendQueueLimit, "SetSendQueueLimit", limit.toString());
                //clientDebugOutput(this, "setSendQueueLimit formatted request");
                if (typeof msg != 'undefined') {
                    this.databytesOutSinceLastConnection += msg.length;
                    this.socket.send(msg);
                    //clientDebugOutput(this, "setSendQueueLimit sent request");
                }
            }
            catch (error) {
                if (error instanceof DOMException) {
                    if (error.name != "InvalidStateError") {
                        //alert("received uncaught DOMException");
                        //throw error;
                    }
                    else {
                        //console.log("Got InvalidStateError");
                    }
                }
                else {
                    //console.log("Got error " + error);
                }

                clientDebugOutput(this, "setSendQueueLimit got error");

                return false;
            }

            return true;
        }
        else {
            clientDebugOutput(this, "setSendQueueLimit not connected");

            return false;
        }
    }

    // TODO: make this type match whatever enum the Mode becomes.
    requestConnectionMode(newConnectionMode: number): boolean {
        // Check that input parameters are valid.
        if ((typeof newConnectionMode != 'number')) {
            // newConnectionMode must be a big integer (long)
            clientDebugOutput(this, "Invalid types to requestConnectionMode");
            return false;
        }

        if (this.connected) {
            //clientDebugOutput(this, "requestConnectionMode connected");
            try {
                let connectionMode = "he";
                switch (newConnectionMode) {
                    case 0:     // Mode.HighEfficiency
                        connectionMode = "he";
                        break;
                    case 1:     // Mode.HighPerformance
                        connectionMode = "hp";
                        break;
                    default:
                        // Invalid connection mode
                        return false;
                }

                const msg = this.SGFormat(SGMessageTypeRequestConnectionMode, "RequestConnectionMode", connectionMode);
                //clientDebugOutput(this, "requestConnectionMode formatted request");
                if (typeof msg != 'undefined') {
                    this.databytesOutSinceLastConnection += msg.length;
                    this.socket.send(msg);
                    //clientDebugOutput(this, "requestConnectionMode sent request");
                }
            }
            catch (error) {
                if (error instanceof DOMException) {
                    if (error.name != "InvalidStateError") {
                        //alert("received uncaught DOMException");
                        //throw error;
                    }
                    else {
                        //console.log("Got InvalidStateError");
                    }
                }
                else {
                    //console.log("Got error " + error);
                }

                clientDebugOutput(this, "requestConnectionMode got error");

                return false;
            }

            return true;
        }
        else {
            clientDebugOutput(this, "requestConnectionMode not connected");

            return false;
        }
    }

    requestNetworkLB(localChannel: string, remoteChannel: string, testSize: number): boolean {
        // Check that input parameters are valid.
        if ((typeof localChannel != 'string') || (typeof remoteChannel != 'string') || (typeof testSize != 'number')) {
            // localChannel and remoteChannelmust be a string and testSize must be a big integer (long)
            clientDebugOutput(this, "Invalid types to requestNetworkLB");
            return false;
        }

        if (this.connected) {
            //clientDebugOutput(this, "requestNetworkLB connected");
            try {
                // TODO: get local channel setup

                // TODO: setup network lb structure

                // TODO: Create test buffer size

                // TODO: Get current ticks plus time delta

                // TODO: Serialize data.

                // TODO: BUG: this function was originally sending with channel and ticks undefined.  It was broken or never implemented in JS
                const ticks = LocalClock.now(); // TODO: check that this is in milliseconds, and then convert to dotnet ticks or not.
                const msg = this.SGFormat(SGMessageTypeRequestNetworkLB, remoteChannel, ticks.toString()+"0000");
                //clientDebugOutput(this, "requestNetworkLB formatted request");
                if (typeof msg != 'undefined') {
                    this.databytesOutSinceLastConnection += msg.length;
                    this.socket.send(msg);
                    //clientDebugOutput(this, "requestNetworkLB sent request");
                }
            }
            catch (error) {
                if (error instanceof DOMException) {
                    if (error.name != "InvalidStateError") {
                        //alert("received uncaught DOMException");
                        //throw error;
                    }
                    else {
                        //console.log("Got InvalidStateError");
                    }
                }
                else {
                    //console.log("Got error " + error);
                }

                clientDebugOutput(this, "requestNetworkLB got error");

                return false;
            }

            return true;
        }
        else {
            clientDebugOutput(this, "requestNetworkLB not connected");

            return false;
        }
    }

}
